import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { makeStyles } from "@material-ui/styles";
import { Button, Container, Grid, Typography } from "@material-ui/core";
import { Helmet } from "react-helmet-async";
import { headRewrite } from "../function/headRewrite";

const useStyles = makeStyles((theme) => ({
  root: {
    background: "#eeeeee",
    color: "#111",
    minHeight: "100vh",
    margin: 0,
    padding: 0,
    fontFamily:
      '"Helvetica Neue", Arial, "Noto Sans JP", "Yu Gothic", sans-serif',
  },
  pageFrame: {
    background: "#eeeeee",
    [theme.breakpoints.down("sm")]: {
      borderLeft: "none",
      borderRight: "none",
    },
  },
  hero: {
    padding: theme.spacing(8, 0, 10),
    borderBottom: "1px solid #c8c8c8",
    position: "relative",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(6, 0, 7),
    },
  },
  section: {
    padding: theme.spacing(10, 0),
    borderBottom: "1px solid #c8c8c8",
    position: "relative",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(7, 0),
    },
  },
  labelRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(4),
    fontSize: 12,
    letterSpacing: "0.12em",
    fontWeight: 700,
    textTransform: "uppercase",
  },
  labelLine: {
    flex: 1,
    height: 1,
    background: "#111",
    opacity: 0.7,
  },
  heroTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", "Hiragino Mincho ProN", serif',
    fontSize: "clamp(30px, 4.8vw, 58px)",
    lineHeight: 1.18,
    letterSpacing: "-0.08em",
    fontWeight: 500,
    marginBottom: theme.spacing(4),
  },
  heading: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", "Hiragino Mincho ProN", serif',
    fontSize: "clamp(28px, 4vw, 48px)",
    lineHeight: 1.45,
    letterSpacing: "-0.05em",
    fontWeight: 500,
    marginBottom: theme.spacing(4),
  },
  subHeading: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", "Hiragino Mincho ProN", serif',
    fontSize: "clamp(22px, 2.8vw, 34px)",
    lineHeight: 1.55,
    letterSpacing: "-0.04em",
    fontWeight: 500,
    marginBottom: theme.spacing(2.5),
  },
  heroBody: {
    maxWidth: 670,
    fontSize: 15,
    lineHeight: 2.1,
    color: "#222",
    marginBottom: theme.spacing(4),
    whiteSpace: "pre-line",
  },
  body: {
    fontSize: 15,
    lineHeight: 2.1,
    color: "#222",
    whiteSpace: "pre-line",
  },
  noteBody: {
    fontSize: 14,
    lineHeight: 2,
    color: "#333",
    whiteSpace: "pre-line",
  },
  buttonRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(4),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "center",
      textAlign: "center",
    },
  },
  primaryButton: {
    background: "#111",
    color: "#eeeeee",
    border: "1px solid #111",
    borderRadius: 0,
    padding: theme.spacing(1.6, 3.5),
    fontWeight: 700,
    boxShadow: "none",
    "&:hover": {
      background: "#eeeeee",
      color: "#111",
      boxShadow: "none",
    },
  },
  secondaryButton: {
    background: "transparent",
    color: "#111",
    border: "1px solid #111",
    borderRadius: 0,
    padding: theme.spacing(1.6, 3.5),
    fontWeight: 700,
    boxShadow: "none",
    "&:hover": {
      background: "#111",
      color: "#eeeeee",
      boxShadow: "none",
    },
  },
  textLink: {
    color: "#111",
    textDecoration: "none",
    borderBottom: "1px solid #111",
    paddingBottom: 4,
    fontSize: 14,
    fontWeight: 700,
  },
  keywordLine: {
    borderTop: "1px solid #111",
    marginTop: theme.spacing(4),
    paddingTop: theme.spacing(2),
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    fontSize: 13,
    color: "#333",
  },
  heroVisual: {
    position: "relative",
    minHeight: 540,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      minHeight: 380,
      marginTop: theme.spacing(6),
    },
  },
  heroCircle: {
    position: "absolute",
    right: -40,
    bottom: 22,
    width: 270,
    height: 270,
    border: "1px solid #111",
    borderRadius: "50%",
    opacity: 0.65,
    [theme.breakpoints.down("sm")]: {
      width: 180,
      height: 180,
      right: -60,
    },
  },
  heroSquare: {
    position: "absolute",
    left: 24,
    top: 44,
    width: 190,
    height: 190,
    border: "1px solid #111",
    opacity: 0.55,
    [theme.breakpoints.down("sm")]: {
      width: 120,
      height: 120,
      left: 0,
    },
  },
  heroNumber: {
    position: "absolute",
    top: 20,
    right: 0,
    writingMode: "vertical-rl",
    fontSize: 13,
    letterSpacing: "0.12em",
    fontWeight: 700,
  },
  heroMock: {
    position: "relative",
    width: "74%",
    maxWidth: 420,
    aspectRatio: "1 / 1",
    border: "1px solid #111",
    background: "rgba(255, 255, 255, 0.18)",
    overflow: "hidden",
    transform: "rotate(2deg)",
    [theme.breakpoints.down("sm")]: {
      width: "84%",
      maxWidth: 320,
    },
  },
  heroMockImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  heroMockLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
  },
  heroMockTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: "clamp(24px, 3vw, 36px)",
    lineHeight: 1.5,
    letterSpacing: "-0.04em",
    fontWeight: 500,
  },
  thesisCard: {
    border: "1px solid #111",
    minHeight: 310,
    padding: theme.spacing(4),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    background: "transparent",
    [theme.breakpoints.down("sm")]: {
      minHeight: "auto",
    },
  },
  thesisLabel: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(2),
  },
  thesisText: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: "clamp(21px, 2.4vw, 30px)",
    lineHeight: 1.72,
    letterSpacing: "-0.03em",
    fontWeight: 500,
  },
  knowledgeGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: theme.spacing(4),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  knowledgeItem: {
    borderLeft: "1px solid #111",
    paddingLeft: theme.spacing(3),
  },
  knowledgeTitle: {
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1.7,
    marginBottom: theme.spacing(1.5),
  },
  brandGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    borderTop: "1px solid #c8c8c8",
    borderLeft: "1px solid #c8c8c8",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  brandItem: {
    borderRight: "1px solid #c8c8c8",
    borderBottom: "1px solid #c8c8c8",
    padding: theme.spacing(2.8, 2.2),
    minHeight: 188,
  },
  brandTag: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(1.4),
  },
  brandTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: 20,
    lineHeight: 1.5,
    letterSpacing: "-0.03em",
    marginBottom: theme.spacing(1.5),
  },
  listBlock: {
    borderTop: "1px solid #111",
  },
  listItem: {
    display: "grid",
    gridTemplateColumns: "72px 260px 1fr",
    gap: theme.spacing(3),
    padding: theme.spacing(3, 0),
    borderBottom: "1px solid #c8c8c8",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      gap: theme.spacing(1.5),
    },
  },
  listIndex: {
    fontSize: 14,
    fontWeight: 700,
    color: "#555",
  },
  listTitle: {
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1.6,
  },
  card: {
    border: "1px solid #111",
    minHeight: 310,
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    background: "transparent",
    [theme.breakpoints.down("sm")]: {
      minHeight: 250,
    },
  },
  cardNumber: {
    fontSize: 24,
    marginBottom: theme.spacing(2),
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: "0.02em",
    marginBottom: theme.spacing(2),
  },
  smallRule: {
    width: 32,
    height: 1,
    background: "#111",
    marginBottom: theme.spacing(3),
  },
  quoteBlock: {
    border: "1px solid #111",
    padding: theme.spacing(5),
    position: "relative",
    background: "#111",
    color: "#eeeeee",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(4, 3),
    },
  },
  quoteMark: {
    fontFamily: '"Times New Roman", serif',
    fontSize: 72,
    lineHeight: 0.8,
    opacity: 0.75,
    marginBottom: theme.spacing(2),
  },
  quoteText: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: "clamp(22px, 3vw, 36px)",
    lineHeight: 1.72,
    letterSpacing: "-0.04em",
    fontWeight: 500,
  },
  inventoryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: theme.spacing(2.5),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  inventoryCard: {
    border: "1px solid #111",
    minHeight: 370,
    padding: theme.spacing(2.5),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  inventoryCardLink: {
    color: "inherit",
    textDecoration: "none",
    transition: "background 180ms ease, transform 180ms ease",
    "&:hover": {
      background: "rgba(255, 255, 255, 0.36)",
      transform: "translateY(-2px)",
    },
  },
  inventoryImage: {
    aspectRatio: "1 / 1",
    width: "100%",
    border: "1px solid #c8c8c8",
    background: "rgba(255,255,255,0.18)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    fontSize: 11,
    lineHeight: 1.8,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(2.5),
    padding: theme.spacing(2),
  },
  inventoryProductImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  inventoryMeta: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(1),
  },
  inventoryTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: 20,
    lineHeight: 1.5,
    letterSpacing: "-0.03em",
    marginBottom: theme.spacing(1.5),
  },
  faqGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  faqItem: {
    border: "1px solid #111",
    padding: theme.spacing(3),
  },
  faqQuestion: {
    fontSize: 17,
    fontWeight: 700,
    lineHeight: 1.8,
    marginBottom: theme.spacing(1.5),
  },
  finalCta: {
    margin: theme.spacing(8, 0),
    border: "1px solid #111",
    padding: theme.spacing(7, 3),
    textAlign: "center",
    position: "relative",
    overflow: "hidden",
  },
  finalTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: "clamp(30px, 4.8vw, 58px)",
    lineHeight: 1.35,
    letterSpacing: "-0.05em",
    marginBottom: theme.spacing(2),
  },
  stripe: {
    position: "absolute",
    right: 20,
    top: 30,
    bottom: 30,
    width: 48,
    background:
      "repeating-linear-gradient(90deg, transparent 0, transparent 7px, #111 8px, #111 9px)",
    opacity: 0.6,
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  indexHero: {
    padding: theme.spacing(8, 0, 10),
    borderBottom: "1px solid #c8c8c8",
  },
  indexIntro: {
    maxWidth: 760,
  },
  guideGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: theme.spacing(3),
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  guideCard: {
    border: "1px solid #111",
    minHeight: 320,
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    color: "#111",
    textDecoration: "none",
    transition: "background 0.2s ease, color 0.2s ease",
    "&:hover": {
      background: "#111",
      color: "#eeeeee",
    },
  },
  guideCategory: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(2),
  },
  guideCardTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: 24,
    lineHeight: 1.55,
    letterSpacing: "-0.04em",
    marginBottom: theme.spacing(2),
  },
  guideCardBody: {
    fontSize: 14,
    lineHeight: 2,
  },
  guideArrow: {
    marginTop: theme.spacing(3),
    fontWeight: 700,
    fontSize: 14,
  },
  notFound: {
    minHeight: "70vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: theme.spacing(8, 0),
  },
}));

const guideArticles = [
  {
    slug: "first_sacai_experience",
    category: "BRAND ENTRY",
    title: "初めてのsacaiをレンタル体験",
    listDescription:
      "見慣れた服の構造をずらし、重ね、組み替える。sacaiを“着ること”から理解する入口ページ。",
    seo: {
      title:
        "初めてのsacaiをレンタル体験｜異素材ミックスを着て知るEnclopedia",
      description:
        "sacaiが気になるけれど、何から見ればいいか分からない方へ。異素材切替、ドッキング、レイヤード構造など、sacaiの魅力を解説し、Enclopediaでレンタルできるアーカイブへ案内します。",
      keyword:
        "初めてのsacai,sacai 初めて,sacai レンタル,sacai 着てみたい,sacai 異素材,sacai ドッキング,sacai アーカイブ,サカイ レンタル,enclopedia,アンクロペディア",
      canonicalPath: "/archive_guide/first_sacai_experience",
    },
    hero: {
      label: "FIRST SACAI EXPERIENCE",
      number: "02 / ENTRY",
      titleLines: ["初めての", "SACAIをレンタル体験。"],
      body:
        "一見すると日常着の延長に見えるのに、\n近づくと異なる素材、用途、服の構造が一着の中で共存している。\nsacaiを初めて知るなら、まずは“着ることで分かる違い”から入るのが最も自然です。",
      keywords: [
        "first experience",
        "hybrid",
        "layering",
        "sacai",
      ],
      visualLabel: "ARCHIVE EXPERIENCE",
      visualTitle: ["見慣れた服の中に、", "違う構造を発見する。"],
      image: "media/archive_fashion/archive_guide2.png",
      imageAlt: "初めてのsacaiをレンタル体験するためのアーカイブガイド画像",
      ctaLabel: "レンタルできるsacaiを見る",
      ctaHref: "https://www.enclopedia-official.com/brand/SACAI（サカイ）/?number=63",
    },
    thesis: {
      text:
        "sacaiは、見慣れた服を“混ぜる”ことで、日常着の見え方を更新するブランドです。",
      keywords: ["hybrid", "structure", "experience"],
    },
    sections: {
      whyThisPage: {
        label: "02 / WHY THIS PAGE?",
        titleLines: [
          "sacaiが気になる。",
          "でも、どこが特別なのか説明しづらい。",
        ],
        body:
          "sacaiの服は、写真だけを見ると“デザインが凝っている服”に見えることがあります。\nしかし実際には、ニットとシャツ、ブルゾンとテーラード、スカートとプリーツのように、異なる服の役割を一着の中で組み替えている。\nその面白さは、着たときの立体感や動きでより明確になります。",
      },
  
      brandOverview: {
        label: "03 / BRAND OVERVIEW",
        titleLines: [
          "異素材を重ねるのではなく、",
          "服の役割を組み替える。",
        ],
        body:
          "sacaiは、日常的で見慣れた服を出発点にしながら、異なる素材や構造を接続することで、まったく新しい輪郭を作るブランドです。\nシャツ、ニット、MA-1、トレンチ、プリーツなど、誰もが知る服の要素を用いながら、その“当たり前の見え方”を静かに変えていきます。",
        knowledgeItems: [
          {
            title: "ドッキングは、装飾ではなく設計。",
            text:
              "異素材の切替は単なるアクセントではありません。前身頃と後身頃、上半身と裾、表地と裏地の役割をずらすことで、服そのものの構造を再編集しています。",
          },
          {
            title: "正面と横、動いた瞬間で印象が変わる。",
            text:
              "sacaiは、静止画よりも身体に乗ったときの見え方が重要です。横から見たボリューム、歩いたときの揺れ、重なった素材の間に生まれる奥行きが魅力になります。",
          },
          {
            title: "定番服を、定番のまま終わらせない。",
            text:
              "ミリタリー、ワーク、ニット、シャツといった身近な服を用いながら、配置や重なりを変えることで、既視感のある服を新しいものとして見せます。",
          },
          {
            title: "初めてほど、“着てみる”意味がある。",
            text:
              "sacaiの服は、写真では細部の面白さに目が向きがちです。実際に着ると、構造が身体にどう作用するかまで理解できます。",
          },
        ],
      },
  
      lineGuide: {
        label: "04 / HOW TO SEE SACAI?",
        titleLines: [
          "sacaiを見るなら、",
          "まず4つの視点から。",
        ],
        items: [
          {
            tag: "ENTRY 01",
            title: "HYBRID CONSTRUCTION",
            text:
              "異なる服の構造を一着に接続する。sacaiを理解する最も基本的な視点です。",
          },
          {
            tag: "ENTRY 02",
            title: "MATERIAL CONTRAST",
            text:
              "ニットと布帛、ミリタリーとプリーツなど、素材の緊張感によって服の表情を作ります。",
          },
          {
            tag: "ENTRY 03",
            title: "VOLUME & LAYER",
            text:
              "一枚の服の中に、レイヤードしているような奥行きを生む。横姿や動きに差が出ます。",
          },
          {
            tag: "ENTRY 04",
            title: "COLLABORATION",
            text:
              "他ブランドとの協業でも、sacaiは単なるロゴ交換ではなく、相手の服を自らの構造へ翻訳します。",
          },
        ],
      },
  
      firstExperience: {
        label: "05 / FIRST EXPERIENCE",
        titleLines: ["初めて着ると、", "何が分かるのか。"],
        intro:
          "sacaiの魅力は、ディテールを読むことと、身体に乗せて構造を感じることの両方で立ち上がります。",
        items: [
          {
            index: "01",
            title: "異素材が切り替わる理由",
            text:
              "素材が違うから目立つのではなく、それぞれが異なる役割を持つことで、一着の中に複数の服の記憶が残ります。",
          },
          {
            index: "02",
            title: "静止画では見えない横顔と動き",
            text:
              "重なりや切替は、正面だけでは理解しきれません。横から見たときのふくらみ、歩いたときの揺れで、sacaiらしさが強く表れます。",
          },
          {
            index: "03",
            title: "日常着の安心感と、少しの違和感",
            text:
              "見慣れた服の要素を残しているから取り入れやすい。一方で、その構造は普通の服とは違う。この二重性がsacaiの入口になります。",
          },
        ],
      },
  
      quote: {
        textLines: [
          "見慣れた服ほど、",
          "sacaiの編集で新しく見える。",
        ],
      },
  
      whyRent: {
        label: "06 / WHY RENT?",
        titleLines: ["sacaiは、", "実物で理解が深まる。"],
        items: [
          {
            number: "01",
            title: "素材の厚みと落ち感が分かる",
            text:
              "異素材の組み合わせは、写真より実物で見たほうが差が明確です。重さ、張り、落ち感まで体験できます。",
          },
          {
            number: "02",
            title: "横姿とレイヤーの立ち上がりを確かめられる",
            text:
              "sacaiの魅力は、正面だけでは完結しません。横からのシルエットや動きの中で、構造がはっきり見えてきます。",
          },
          {
            number: "03",
            title: "購入前に生活との距離を測れる",
            text:
              "デザイン性が高い服ほど、自分の日常にどう入るかを試す価値があります。レンタルは、その判断材料になります。",
          },
        ],
      },
  
      inventory: {
        label: "07 / AVAILABLE ARCHIVE",
        titleLines: ["Enclopediaで", "体験できるsacai。"],
        body:
          "実在庫への導線は、“sacaiらしさを理解する続き”として設計します。まず知り、次に実物で確かめる流れを作ります。",
        items: [
          {
            meta: "sacai × WTAPS / 2025SS",
            title: "コラボレーションジャケット",
            text:
              "ワークやミリタリーの文脈を持つジャケットに、sacaiらしい構造のずれとレイヤード感を重ねた一着。",
            href: "https://www.enclopedia-official.com/product/079f1c35-7f8d-45e4-aca8-fb7da822e05b",
            image:
              "media/product/1773151184.3163898079f1c35-7f8d-45e4-aca8-fb7da822e05b.png",
          },
          {
            meta: "sacai",
            title: "異素材切替ワンピース",
            text:
              "異なる素材が同居することで、静止した状態と動いたときの見え方が変わるsacaiらしいアーカイブ。",
            href: "https://www.enclopedia-official.com/product/7d7cd289-9842-470a-a417-a43c1b7750b6",
            image:
              "media/product/1778919202.29588277d7cd289-9842-470a-a417-a43c1b7750b6.png",
          },
        ],
      },
  
      faq: {
        label: "08 / FAQ",
        titleLines: ["初めての人が、", "気になりやすいこと。"],
        items: [
          {
            question: "sacaiは、どのアイテムから見るのがおすすめですか？",
            answer:
              "初めてなら、ニット、スカート、ジャケットなど、日常着の形式を残しながら構造の違いが見えやすいアイテムがおすすめです。",
          },
          {
            question: "写真と実物で印象はかなり変わりますか？",
            answer:
              "変わります。特に異素材切替やレイヤード構造は、厚みや横からの見え方、動いたときの揺れで印象が大きく変化します。",
          },
          {
            question: "デザイン性が強くて、着こなせるか不安です。",
            answer:
              "sacaiは見慣れた服を土台にしているため、実際に着ると意外に日常へ落とし込みやすい一着も多いです。レンタルで確認するのが合理的です。",
          },
          {
            question: "レンタル後に購入もできますか？",
            answer:
              "対象アイテムは、レンタル体験を経たうえで購入相談へ進める導線を想定しています。",
          },
        ],
      },
  
      finalCta: {
        titleLines: [
          "初めてのsacaiを、",
          "レンタルから始める。",
        ],
        body:
          "見慣れた服が、構造によって別の表情を持つ。\nその面白さを、Enclopediaで実際に体験してください。",
        buttonLabel: "レンタルできるsacaiを見る",
        buttonHref: "https://www.enclopedia-official.com/brand/SACAI（サカイ）/?number=63",
      },
    },
  },
  {
    slug: "first_comme_des_garcons_experience",
    category: "BRAND ENTRY",
    title: "初めてのCOMME des GARÇONSをレンタル体験",
    listDescription:
      "難しそうに見えるギャルソンを、“買う前に着る”ことで知るための入口ページ。",
    seo: {
      title:
        "初めてのコムデギャルソンをレンタル体験｜気軽に着て知るEnclopedia",
      description:
        "COMME des GARÇONSが気になるけれど、何から見ればいいか分からない方へ。ブランドの魅力、主要ラインの違い、初めて着ると分かることを解説し、Enclopediaでレンタルできるコムデギャルソンのアーカイブへ案内します。",
      keyword:
        "初めてのコムデギャルソン,コムデギャルソン 初めて,コムデギャルソン どんなブランド,コムデギャルソン 着てみたい,コムデギャルソン レンタル,COMME des GARCONS レンタル,コムデギャルソン オムプリュス,enclopedia,アンクロペディア",
      canonicalPath: "/archive_guide/first_comme_des_garcons_experience",
    },
    hero: {
      label: "FIRST COMME DES GARÇONS EXPERIENCE",
      number: "01 / ENTRY",
      titleLines: ["初めての", "COMME des GARÇONSをレンタル体験。"],
      body:
        "難しそうに見えるコムデギャルソンを、\n“買う前に着る”ことで知るための入口ページです。\nブランドの魅力、ラインの違い、初めて着たときに分かることを整理しながら、Enclopediaで体験できるアーカイブへつなげます。",
      keywords: [
        "first experience",
        "archive",
        "wear before own",
        "comme des garçons",
      ],
      visualLabel: "ARCHIVE EXPERIENCE",
      visualTitle: ["気になっていた服を、", "まず着て理解する。"],
      image: "media/archive_fashion/archive_guide1.png",
      imageAlt: "初めてのCOMME des GARÇONSをレンタル体験するためのアーカイブガイド画像",
      ctaLabel: "レンタルできるコムデギャルソンを見る",
      ctaHref: "https://www.enclopedia-official.com/brand/COMME%20DES%20GARCONS（コムデギャルソン）/?number=10",
    },
    thesis: {
      text:
        "ギャルソンは、知ってから着る服ではなく、着ることで見え方が変わる服でもあります。",
      keywords: ["entry", "experience", "understanding"],
    },
    sections: {
      whyThisPage: {
        label: "02 / WHY THIS PAGE?",
        titleLines: [
          "コムデギャルソンが気になる。",
          "でも、何から見ればいいか分からない。",
        ],
        body:
          "COMME des GARÇONSは、服を“分かりやすく消費する”ブランドではありません。\nだからこそ、初めて触れる人には少し遠く見えることがあります。\nこのページでは、ブランドを難解な知識としてではなく、“着ることで理解が始まる文化”として整理します。",
      },
      brandOverview: {
        label: "03 / BRAND OVERVIEW",
        titleLines: ["“難しいブランド”ではなく、", "見方を変えるブランド。"],
        body:
          "コムデギャルソンは、既存の美しさや服の“普通”を問い直してきたブランドです。\n黒、非対称、解体、膨らみ、余白。\n目立つ装飾だけではなく、服の構造そのものを更新する姿勢に大きな特徴があります。",
        knowledgeItems: [
          {
            title: "美しさを、別の角度から見る。",
            text:
              "端正さだけではなく、歪みや余白、未完成に見える形の中にも価値を見出す。ギャルソンは“なぜ美しいのか”を服で問い続けます。",
          },
          {
            title: "服より先に、身体の見え方を変える。",
            text:
              "服が身体を整えるのではなく、服が身体の輪郭や存在感を組み替える。その感覚は、実際に着ると最も分かりやすく立ち上がります。",
          },
          {
            title: "アーカイブで評価される理由。",
            text:
              "一過性のトレンドではなく、コレクションごとに問いがあり、時間が経っても読み直せる。だから過去作品が資料として残ります。",
          },
          {
            title: "初心者ほど、“着てみる”意味がある。",
            text:
              "写真で難しく見える服ほど、身体に乗せると印象が変わることがあります。最初の理解をレンタルが補助します。",
          },
        ],
      },
      lineGuide: {
        label: "04 / WHICH LINE?",
        titleLines: [
          "“コムデギャルソン”は一つではない。",
          "初めてなら、入口の違いを知る。",
        ],
        items: [
          {
            tag: "ENTRY 01",
            title: "COMME des GARÇONS",
            text:
              "ブランドの中核。既存の美しさや服の“普通”を問い直す姿勢が最も濃く表れます。",
          },
          {
            tag: "ENTRY 02",
            title: "COMME des GARÇONS HOMME PLUS",
            text:
              "メンズにおける実験の中心。テーラリング、身体、装飾を大胆に再構成します。",
          },
          {
            tag: "ENTRY 03",
            title: "COMME des GARÇONS HOMME DEUX",
            text:
              "日本の背広,日常に近いスーツやジャケットの中で、ギャルソンらしい緊張感を体験しやすいラインです。",
          },
          {
            tag: "ENTRY 04",
            title: "COMME des GARÇONS SHIRT / COMME des GARÇONS COMME des GARÇONS",
            text:
              "より軽やかな入口。日常着に近い距離で、ギャルソンの発想に触れられます。",
          },
        ],
      },
      firstExperience: {
        label: "05 / FIRST EXPERIENCE",
        titleLines: ["初めて着ると、", "何が分かるのか。"],
        intro:
          "ギャルソンの魅力は、説明を読むだけでは完結しません。身体に乗せた瞬間に初めて分かる“服との距離感”があります。",
        items: [
          {
            index: "01",
            title: "“黒い服”の奥にある違い",
            text:
              "黒を基調にしていても、素材の厚み、切替、襟の角度、分量感によって印象は大きく変わります。ギャルソンは色よりも構造で服を記憶に残します。",
          },
          {
            index: "02",
            title: "身体を整えるより、見え方を更新する",
            text:
              "一般的な服が身体を美しく整えるために働くのに対し、ギャルソンは身体の輪郭や存在感そのものを問い直すことがあります。",
          },
          {
            index: "03",
            title: "違和感は、難しさではなく入口になる",
            text:
              "最初は“少し変わっている”と感じる服でも、その違和感がどこから来るかを知ると、見方が変わります。",
          },
        ],
      },
      quote: {
        textLines: [
          "所有の前に、体験する。",
          "そのほうが、ギャルソンはずっと近くなる。",
        ],
      },
      whyRent: {
        label: "06 / WHY RENT?",
        titleLines: ["初めてのギャルソンほど、", "レンタルと相性がいい。"],
        items: [
          {
            number: "01",
            title: "写真だけでは分からない",
            text:
              "サイズ表よりも、身体に乗せたときの重心や空気感で印象が変わる。まず着ることで、理解の解像度が上がります。",
          },
          {
            number: "02",
            title: "違和感が入口になる",
            text:
              "少し難しく見える服でも、その違和感がどこから来るかを知ると、魅力に変わります。",
          },
          {
            number: "03",
            title: "所有前の判断材料になる",
            text:
              "高額なアーカイブほど、素材感や生活との距離を確かめたうえで選ぶ体験が重要です。",
          },
        ],
      },
      inventory: {
        label: "07 / AVAILABLE ARCHIVE",
        titleLines: ["Enclopediaで", "体験できる一着。"],
        body:
          "実在庫への導線は、“ブランド説明の続き”として設計します。読むだけで終わらず、気になった服をそのまま体験へ進めます。",
        items: [
          {
            meta: "COMME des GARÇONS",
            title: "Archive Item 01",
            text:
              "初めてのコムデギャルソン体験につながる、実物で確かめたいアーカイブアイテム。",
            href: "https://www.enclopedia-official.com/product/ee262ae8-20a8-4065-b227-e5d392176a03",
            image:
              "media/product/1727876263.9000912ee262ae8-20a8-4065-b227-e5d392176a03.jpg",
          },
          {
            meta: "COMME des GARÇONS",
            title: "Archive Item 02",
            text:
              "写真だけでは分かりにくいシルエットや素材感を、レンタルで体験できる一着。",
            href: "https://www.enclopedia-official.com/product/6d4e73e7-6ae8-4bc1-bbac-69a855493a72",
            image:
              "media/product/1755779605.73427256d4e73e7-6ae8-4bc1-bbac-69a855493a72.jpg",
          },
          {
            meta: "COMME des GARÇONS",
            title: "Archive Item 03",
            text:
              "ブランドらしい違和感や構造を、最初の一着として試しやすいアーカイブ。",
            href: "https://www.enclopedia-official.com/product/25b45365-050d-4281-9ad0-536f21f0fc99",
            image:
              "media/product/1758033139.971845925b45365-050d-4281-9ad0-536f21f0fc99.jpg",
          },
          {
            meta: "COMME des GARÇONS",
            title: "Archive Item 04",
            text:
              "詳細ページで状態やサイズを確認しながら、レンタル体験へ進めるアーカイブ。",
            href: "https://www.enclopedia-official.com/product/23d8dac3-41aa-42d7-8d6e-1fa68ab20251",
            image: "media/product/1776929485.797350423d8dac3-41aa-42d7-8d6e-1fa68ab20251.png",
          },
        ],
      },
      faq: {
        label: "08 / FAQ",
        titleLines: ["初めての人が、", "気になりやすいこと。"],
        items: [
          {
            question: "初めてでも、いきなりレンタルして大丈夫ですか？",
            answer:
              "大丈夫です。Enclopediaは、アーカイブに詳しい人だけでなく、“気になっているが、まだ着たことがない人”の入口として設計しています。",
          },
          {
            question: "コムデギャルソンは難しそうで、自分に合うか不安です。",
            answer:
              "その不安こそ、体験が役立つ部分です。写真や寸法だけでは分からない相性を、まず着て確かめられます。",
          },
          {
            question: "どのラインから見るのがおすすめですか？",
            answer:
              "日常との距離が近いHOMME DEUXやSHIRT、より強い体験を求めるならCOMME des GARCONS HOMME PLUSがおすすめです。",
          },
          {
            question: "レンタル後に購入もできますか？",
            answer:
              "対象アイテムは、レンタル体験を経たうえで購入相談へ進める導線を想定しています。",
          },
        ],
      },
      finalCta: {
        titleLines: [
          "初めてのコムデギャルソンを、",
          "レンタルから始める。",
        ],
        body:
          "難しそうに見える服ほど、実際に着ることで距離が縮まります。\nEnclopediaで、気になっていた一着を体験してください。",
        buttonLabel: "レンタルできるコムデギャルソンを見る",
        buttonHref: "https://www.enclopedia-official.com/brand/COMME%20DES%20GARCONS（コムデギャルソン）/?number=10",
      },
    },
  },
  {
    slug: "first_comme_des_garcons_homme_plus_experience",
    category: "BRAND ENTRY",
    title: "初めてのCOMME des GARÇONS HOMME PLUSをレンタル体験",
    listDescription:
      "テーラリングを壊し、身体の見え方を変える。COMME des GARCONS HOMME PLUSを“着ること”から理解する入口ページ。",
    seo: {
      title:
        "初めてのコムデギャルソンオムプリュスをレンタル体験｜COMME des GARCONS HOMME PLUSを着て知るEnclopedia",
      description:
        "COMME des GARÇONS HOMME PLUSが気になるけれど、どんなブランドか分からない方へ。コムデギャルソンオムプリュスの魅力、テーラリングの再構成、縮絨・再構築・コレクション性を分かりやすく解説し、Enclopediaでレンタルできるアーカイブへ案内します。",
      keyword:
        "初めてのコムデギャルソンオムプリュス,コムデギャルソンオムプリュス 初めて,オムプリュス 初めて,COMME des GARCONS HOMME PLUS 初めて,コムデギャルソンオムプリュス とは,オムプリュス どんなブランド,HOMME PLUS どんなブランド,コムデギャルソンオムプリュス レンタル,HOMME PLUS レンタル,オムプリュス 着てみたい,コムデギャルソンオムプリュス アーカイブ,HOMME PLUS アーカイブ,enclopedia,アンクロペディア",
      canonicalPath:
        "/archive_guide/first_comme_des_garcons_homme_plus_experience",
    },
    hero: {
      label: "FIRST COMME des GARCONS HOMME PLUS EXPERIENCE",
      number: "04 / ENTRY",
      titleLines: [
        "初めての",
        "COMME des GARÇONS HOMME PLUSをレンタル体験。",
      ],
      body:
        "コムデギャルソン オムプリュスは、\n“メンズ服らしさ”を守るブランドではなく、\nテーラード、パンツ、身体の輪郭を更新し続けるブランドです。\n初めて知るなら、まずは“着ることで分かる違和感”から入るのが自然です。",
      keywords: [
        "first experience",
        "tailoring",
        "body",
        "homme plus",
      ],
      visualLabel: "ARCHIVE EXPERIENCE",
      visualTitle: [
        "普通のメンズ服が、",
        "なぜここまで変わるのか。",
      ],
      image: "media/archive_fashion/archive_guide3.png",
      imageAlt:
        "初めてのCOMME des GARÇONS HOMME PLUSをレンタル体験するためのアーカイブガイド画像",
      ctaLabel: "レンタルできるCOMME des GARCONS HOMME PLUSを見る",
      ctaHref:
        "https://www.enclopedia-official.com/brand/COMME%20DES%20GARCONS%20HOMME%20PLUS（コムデギャルソンオムプリュス）/?number=11",
    },
    thesis: {
      text:
        "COMME des GARCONS HOMME PLUSは、男性服を飾るのではなく、男性服の前提そのものを組み替えるブランドです。",
      keywords: ["menswear", "reconstruction", "experience"],
    },
    sections: {
      whyThisPage: {
        label: "02 / WHY THIS PAGE?",
        titleLines: [
          "COMME des GARCONS HOMME PLUSが気になる。",
          "でも、どこが特別なのか説明しづらい。",
        ],
        body:
          "COMME des GARÇONS HOMME PLUSは、ファッション好きの間では強く評価される一方、初めて見る人には“少し難しい服”に映ることがあります。\nしかし、その魅力は専門知識がないと分からないものではありません。\nテーラードジャケット、パンツ、シャツといった見慣れた男性服が、少しずつ壊され、ずらされ、再構成されている。その変化を着ることで理解するための入口が、このページです。",
      },
  
      brandOverview: {
        label: "03 / BRAND OVERVIEW",
        titleLines: [
          "男らしさを強調するのではなく、",
          "男性服の型を問い直す。",
        ],
        body:
          "COMME des GARCONS HOMME PLUSは、1984年に始まったコムデギャルソンのメンズコレクションラインです。\nジャケット、パンツ、スーツ、コートといった男性服の基本形を出発点にしながら、縮絨、再構築、極端な分量、非対称、装飾、柄の衝突を通して、その“普通”を更新してきました。\nだからこそ、COMME des GARCONS HOMME PLUSは単なるメンズブランドではなく、男性服の見え方を変えるコレクションとしてアーカイブで語られ続けています。",
        knowledgeItems: [
          {
            title: "テーラードは、整えるためだけの服ではない。",
            text:
              "COMME des GARCONS HOMME PLUSでは、ジャケットが身体を端正に見せるだけでなく、膨らませ、ずらし、時には歪ませることで、着る人の輪郭そのものを変えます。",
          },
          {
            title: "縮絨・再構築は、装飾ではなく思想の可視化。",
            text:
              "生地を縮ませる、複数の衣服を接続する、既存の形を崩す。こうした技法は“変わったデザイン”ではなく、既成概念を疑う姿勢として現れます。",
          },
          {
            title: "毎シーズン、服に問いがある。",
            text:
              "COMME des GARCONS HOMME PLUSは、コレクションごとにテーマが明確です。花、少年性、宗教性、光、ジェンダー、歴史。服はテーマの説明ではなく、その問いの造形として現れます。",
          },
          {
            title: "アーカイブが面白いのは、変化を比較できるから。",
            text:
              "90年代、2000年代、近年作では、同じCOMME des GARCONS HOMME PLUSでも表現が違います。過去作を追うと、男性服の更新がどのように積み重なったかが見えてきます。",
          },
        ],
      },
  
      lineGuide: {
        label: "04 / HOW TO SEE COMME des GARCONS HOMME PLUS?",
        titleLines: [
          "COMME des GARCONS HOMME PLUSを見るなら、",
          "まず4つの視点から。",
        ],
        items: [
          {
            tag: "ENTRY 01",
            title: "TAILORING",
            text:
              "ジャケットやスーツをどう崩し、どう再構成しているか。COMME des GARCONS HOMME PLUSを読む最初の視点です。",
          },
          {
            tag: "ENTRY 02",
            title: "BODY & PROPORTION",
            text:
              "身体をスマートに整えるのではなく、肩、背中、裾、パンツの分量を変えて、存在感そのものを作ります。",
          },
          {
            tag: "ENTRY 03",
            title: "MATERIAL & PROCESS",
            text:
              "縮絨、パッチワーク、ブリーチ、ドッキング。素材や加工は、シーズンの思想を服に定着させる手段になります。",
          },
          {
            tag: "ENTRY 04",
            title: "COLLECTION THEME",
            text:
              "コレクションのテーマを見ると、単体では奇抜に見える服が、全体の流れの中で理解しやすくなります。",
          },
        ],
      },
  
      firstExperience: {
        label: "05 / FIRST EXPERIENCE",
        titleLines: ["初めて着ると、", "何が分かるのか。"],
        intro:
          "COMME des GARCONS HOMME PLUSは、写真で見て“強い服”に感じても、身体に乗せるとその構造の理由が見えやすくなるブランドです。",
        items: [
          {
            index: "01",
            title: "ジャケットなのに、身体の見え方が変わる",
            text:
              "肩幅、着丈、襟、身頃の膨らみ。わずかな構造の違いで、普通のテーラードとは異なる重心が生まれます。",
          },
          {
            index: "02",
            title: "パンツの分量が、歩き方まで変える",
            text:
              "ワイド、サルエル、極端なシルエット。COMME des GARCONS HOMME PLUSではパンツも単なる下半身の服ではなく、全体の造形を支える重要な要素です。",
          },
          {
            index: "03",
            title: "コレクションのテーマが、着た瞬間に立ち上がる",
            text:
              "花柄、光沢、装飾、重なり。単品で見たときより、身体に乗せたときのほうが、シーズンの意図が鮮明に感じられることがあります。",
          },
        ],
      },
  
      quote: {
        textLines: [
          "男性服を、",
          "もう一度発明する。",
        ],
      },
  
      whyRent: {
        label: "06 / WHY RENT?",
        titleLines: [
          "初めてのCOMME des GARCONS HOMME PLUSほど、",
          "レンタルと相性がいい。",
        ],
        items: [
          {
            number: "01",
            title: "写真では、分量と重心が読み切れない",
            text:
              "COMME des GARCONS HOMME PLUSは平面で見るだけでは魅力が伝わりにくい服が多いです。身体に乗せることで、丈、幅、布量の意味が分かります。",
          },
          {
            number: "02",
            title: "難しそうな服ほど、意外に馴染むことがある",
            text:
              "強い造形に見えても、実際に着ると自分の体格や日常のスタイリングに自然に入る場合があります。",
          },
          {
            number: "03",
            title: "高額なアーカイブを、まず経験として理解できる",
            text:
              "希少性だけで判断せず、なぜこの一着に惹かれるのかを着用体験から確かめられます。",
          },
        ],
      },
  
      inventory: {
        label: "07 / AVAILABLE ARCHIVE",
        titleLines: ["Enclopediaで", "体験できるCOMME des GARCONS HOMME PLUS。"],
        body:
          "実在庫への導線は、“COMME des GARCONS HOMME PLUSを知る続き”として設計します。まず背景を理解し、次に実物で確かめる流れを作ります。",
        items: [
          {
            meta: "COMME des GARCONS HOMME PLUS / 2022AW NOMAD",
            title: "NOMAD期 縮絨テーラードジャケット",
            text:
              "自由に移動し、どこにも属さない“ノマド”の感覚を、縮絨ウールの質感とテーラリングで体感できる一着。",
            href:
              "https://www.enclopedia-official.com/product/a2e91d68-c546-4aad-bdb5-225cf3ced675",
            image:
              "media/product/1774349558.1299286a2e91d68-c546-4aad-bdb5-225cf3ced675.png",
          },
          {
            meta: "COMME des GARCONS HOMME PLUS / 2024SS Beyond reality",
            title: "リーフプリーツジャケット",
            text:
              "“現実を超える”というテーマのもと、テーラードの再構築とリーフモチーフを重ねた2024SSらしいジャケット。",
            href:
              "https://www.enclopedia-official.com/product/f62074f2-f7e3-4b81-b277-c32e536a6684",
            image:
              "media/product/1763042723.0801384f62074f2-f7e3-4b81-b277-c32e536a6684.jpg",
          },
        ],
      },
  
      faq: {
        label: "08 / FAQ",
        titleLines: ["初めての人が、", "気になりやすいこと。"],
        items: [
          {
            question:
              "COMME des GARÇONS HOMME PLUSは、どんなブランドですか？",
            answer:
              "コムデギャルソンのメンズコレクションラインで、テーラリング、身体の見え方、素材加工、装飾を大胆に再構成するブランドです。",
          },
          {
            question:
              "COMME des GARCONS HOMME PLUSと通常のコムデギャルソンは何が違いますか？",
            answer:
              "COMME des GARCONS HOMME PLUSはメンズコレクションの実験性が最も強く現れるラインの一つです。男性服の形式を前提にしながら、その型を崩し、更新します。",
          },
          {
            question:
              "初めてなら、どんなアイテムから見るのがおすすめですか？",
            answer:
              "ジャケット、パンツ、シャツなど、見慣れた男性服の形式を保ちながら、構造の違いが分かるアイテムがおすすめです。",
          },
          {
            question:
              "COMME des GARCONS HOMME PLUSは、レンタルで試す意味がありますか？",
            answer:
              "あります。写真では伝わりにくい布量、重心、立体感、着たときの違和感を確かめられるため、初めての体験と相性が良いです。",
          },
          {
            question: "レンタル後に購入もできますか？",
            answer:
              "対象アイテムは、レンタル体験を経たうえで購入相談へ進める導線を想定しています。",
          },
        ],
      },
  
      finalCta: {
        titleLines: [
          "初めてのCOMME des GARCONS HOMME PLUSを、",
          "レンタルから始める。",
        ],
        body:
          "普通のメンズ服に見えて、着ると世界が少し変わる。\nEnclopediaで、コムデギャルソン オムプリュスのアーカイブを体験してください。",
        buttonLabel: "レンタルできるCOMME des GARCONS HOMME PLUSを見る",
        buttonHref:
          "https://www.enclopedia-official.com/brand/COMME%20DES%20GARCONS%20HOMME%20PLUS（コムデギャルソンオムプリュス）/?number=11",
      },
    },
  },
  {
    slug: "first_maison_margiela_experience",
    category: "BRAND ENTRY",
    title: "初めてのMaison Margielaをレンタル体験",
    listDescription:
      "解体、再構築、匿名性。Maison Margielaを“着ること”から理解する入口ページ。",
    seo: {
      title:
        "初めてのメゾンマルジェラをレンタル体験｜マルジェラの魅力を着て知るEnclopedia",
      description:
        "Maison Margielaが気になるけれど、どんなブランドか分からない方へ。解体・再構築、匿名性、Tabi、アーティザナルなど、メゾンマルジェラの魅力を分かりやすく解説し、Enclopediaでレンタルできるアーカイブへ案内します。",
      keyword:
        "初めてのメゾンマルジェラ,メゾンマルジェラ 初めて,マルジェラ 初めて,マルジェラ 初心者,メゾンマルジェラ とは,マルジェラ どんなブランド,マルジェラ レンタル,Maison Margiela レンタル,マルジェラ 着てみたい,マルジェラ アーカイブ,Maison Margiela アーカイブ,enclopedia,アンクロペディア",
      canonicalPath: "/archive_guide/first_maison_margiela_experience",
    },
    hero: {
      label: "FIRST MAISON MARGIELA EXPERIENCE",
      number: "03 / ENTRY",
      titleLines: ["初めての", "Maison Margielaをレンタル体験。"],
      body:
        "一見すると静かでミニマル。\nけれど近づくと、縫い代、裏側、既製服の形そのものが少しずつずれている。\nMaison Margielaを初めて知るなら、まずは“着ることで分かる違和感”から入るのが自然です。",
      keywords: [
        "first experience",
        "deconstruction",
        "anonymity",
        "maison margiela",
      ],
      visualLabel: "ARCHIVE EXPERIENCE",
      visualTitle: ["見慣れた服の中に、", "構造の違和感を見つける。"],
      image: "media/archive_fashion/archive_guide4.png",
      imageAlt:
        "初めてのMaison Margielaをレンタル体験するためのアーカイブガイド画像",
      ctaLabel: "レンタルできるMaison Margielaを見る",
      ctaHref:
        "https://www.enclopedia-official.com/brand/MAISON%20MARGIELA（メゾンマルジェラ）/?number=47",
    },
    thesis: {
      text:
        "Maison Margielaは、服の“完成形”を疑うことで、日常着の見え方を更新するブランドです。",
      keywords: ["deconstruction", "everyday clothes", "experience"],
    },
    sections: {
      whyThisPage: {
        label: "02 / WHY THIS PAGE?",
        titleLines: [
          "マルジェラが気になる。",
          "でも、Tabiや4ステッチ以外はまだよく分からない。",
        ],
        body:
          "Maison Margielaは、名前だけは知っていても、何が特別なのかを説明しづらいブランドの一つです。\nTabiブーツや4ステッチは有名ですが、本質はそこだけではありません。\nこのページでは、マルジェラを“難解な前衛ブランド”ではなく、“見慣れた服の見方を変えるブランド”として整理します。",
      },
  
      brandOverview: {
        label: "03 / BRAND OVERVIEW",
        titleLines: [
          "完成された服を壊すのではなく、",
          "服の成り立ちを見せる。",
        ],
        body:
          "Maison Margielaは、縫い代、裏地、仮縫い、古着、既製服といった通常は隠れる要素を表へ引き出し、服の構造そのものを見せてきたブランドです。\nその結果、日常的なジャケットやパンツでさえ、少し違う角度から見えるようになる。\nマルジェラの魅力は、“奇抜さ”よりも、普通の服の見え方を静かに揺らすところにあります。",
        knowledgeItems: [
          {
            title: "解体は、壊すことではなく読むこと。",
            text:
              "裏地や縫製、服が成立するまでの痕跡をあえて見せることで、当たり前だった服の構造を読み直します。",
          },
          {
            title: "匿名性は、服そのものを前に出すための方法。",
            text:
              "派手なロゴや作者の顔を強調する代わりに、服そのものの存在感を際立たせる。匿名性はマルジェラの重要な視点です。",
          },
          {
            title: "日常着を出発点にしているから、入口がある。",
            text:
              "ジャケット、シャツ、パンツ、スニーカーなど、見慣れた衣服を土台にしながら、その意味を少しずつずらしていきます。",
          },
          {
            title: "アーカイブで見ると、思想の変化が分かる。",
            text:
              "過去作を追うと、脱構築、匿名性、再利用、手仕事といった考え方が、時期ごとにどのように形になったかが見えてきます。",
          },
        ],
      },
  
      lineGuide: {
        label: "04 / HOW TO SEE MARGIELA?",
        titleLines: [
          "Maison Margielaを見るなら、",
          "まず4つの視点から。",
        ],
        items: [
          {
            tag: "ENTRY 01",
            title: "DECONSTRUCTION",
            text:
              "縫い代、裏地、切替、仮縫い。服の内側を表に出し、“完成された服”という前提を揺さぶります。",
          },
          {
            tag: "ENTRY 02",
            title: "ANONYMITY",
            text:
              "4ステッチや白いタグに象徴される匿名性。ブランドを誇示するのではなく、服の存在を前面に出します。",
          },
          {
            tag: "ENTRY 03",
            title: "EVERYDAY RE-READING",
            text:
              "ジャケットやパンツ、コートのような日常着を、少しのずれや再構成によってまったく違う服に見せます。",
          },
          {
            tag: "ENTRY 04",
            title: "HOUSE CODES",
            text:
              "Tabi、Artisanal、Bianchettoなど、ブランドの象徴的なコードを知ると、作品の読み方が一段深まります。",
          },
        ],
      },
  
      firstExperience: {
        label: "05 / FIRST EXPERIENCE",
        titleLines: ["初めて着ると、", "何が分かるのか。"],
        intro:
          "Maison Margielaの服は、写真で見たときより、身体に乗せた瞬間に“なぜこの形なのか”が理解しやすくなります。",
        items: [
          {
            index: "01",
            title: "一見シンプルなのに、細部に違和感がある",
            text:
              "ポケットの位置、切替線、丈のバランス、縫製の見せ方。目立つ装飾がなくても、服の輪郭に小さな異物感が残ります。",
          },
          {
            index: "02",
            title: "身体の整え方が、普通の服と少し違う",
            text:
              "身体をきれいに見せるためだけではなく、身体と服の距離そのものを再設計する。その感覚は着用時に最も分かりやすく現れます。",
          },
          {
            index: "03",
            title: "定番とアーカイブの見え方が変わる",
            text:
              "Tabiや4ステッチだけでなく、過去のジャケット、パンツ、再構築された衣服を見る目が変わります。“なぜ残るのか”を体験として理解できます。",
          },
        ],
      },
  
      quote: {
        textLines: [
          "目立つための服ではなく、",
          "見え方を変えるための服。",
        ],
      },
  
      whyRent: {
        label: "06 / WHY RENT?",
        titleLines: ["初めてのマルジェラほど、", "レンタルで理解が深まる。"],
        items: [
          {
            number: "01",
            title: "写真では、構造の面白さが伝わりきらない",
            text:
              "素材の厚み、縫製の痕跡、立体感、着たときの余白。マルジェラは実物で見るほど理解が進むブランドです。",
          },
          {
            number: "02",
            title: "日常に入るかを確かめられる",
            text:
              "前衛的に見える服でも、実際に着ると意外に生活へ馴染むことがあります。レンタルはその距離を測る手段になります。",
          },
          {
            number: "03",
            title: "定番を“消費”せず、背景ごと体験できる",
            text:
              "有名だから選ぶのではなく、なぜ評価されてきたのかを、着用を通じて確かめられます。",
          },
        ],
      },
  
      inventory: {
        label: "07 / AVAILABLE ARCHIVE",
        titleLines: ["Enclopediaで", "体験できるMaison Margiela。"],
        body:
          "実在庫への導線は、“マルジェラを知る続き”として設計します。まず背景を理解し、次に実物で確かめる流れを作ります。",
        items: [
          {
            meta: "MAISON MARGIELA / 2024SS",
            title: "ワイドハーフパンツ",
            text:
              "一見シンプルなボトムスの中に、分量感とシルエットのずれが残るMaison Margielaらしい一着。",
            href:
              "https://www.enclopedia-official.com/product/33889388-65dc-4d96-a3a7-388346529a6b",
            image:
              "media/product/1762182245.869212933889388-65dc-4d96-a3a7-388346529a6b.jpg",
          },
        ],
      },
  
      faq: {
        label: "08 / FAQ",
        titleLines: ["初めての人が、", "気になりやすいこと。"],
        items: [
          {
            question: "Maison Margielaは、どんなブランドですか？",
            answer:
              "既存の服の構造や価値観を問い直してきたブランドです。解体、再構築、匿名性、手仕事といった視点が重要になります。",
          },
          {
            question: "初めてなら、どのアイテムから見るのがおすすめですか？",
            answer:
              "ジャケット、パンツ、シャツなど、日常着に近い形式を持ちながら、どこかに違和感が残るアイテムがおすすめです。",
          },
          {
            question: "Tabiやバッグ以外の服もレンタルできますか？",
            answer:
              "はい。Enclopediaでは衣服のアーカイブ体験を重視しており、ジャケットやパンツなどのウェアも扱います。",
          },
          {
            question: "レンタル後に購入もできますか？",
            answer:
              "対象アイテムは、レンタル体験を経たうえで購入相談へ進める導線を想定しています。",
          },
        ],
      },
  
      finalCta: {
        titleLines: [
          "初めてのMaison Margielaを、",
          "レンタルから始める。",
        ],
        body:
          "難しそうに見える服ほど、実際に着ることで意味が近くなる。\nEnclopediaで、マルジェラのアーカイブを体験してください。",
        buttonLabel: "レンタルできるMaison Margielaを見る",
        buttonHref:
          "https://www.enclopedia-official.com/brand/MAISON%20MARGIELA（メゾンマルジェラ）/?number=47",
      },
    },
  },
  {
    slug: "first_junya_watanabe_experience",
    category: "BRAND ENTRY",
    title: "初めてのJUNYA WATANABEをレンタル体験",
    listDescription:
      "パッチワーク、再構築、デニム、コラボレーション。JUNYA WATANABEを“着ること”から理解する入口ページ。",
    seo: {
      title:
        "初めてのジュンヤワタナベをレンタル体験｜JUNYA WATANABEを着て知るEnclopedia",
      description:
        "JUNYA WATANABEが気になるけれど、どんなブランドか分からない方へ。ジュンヤワタナベの魅力、JUNYA WATANABE MAN、eYe、パッチワーク、再構築、デニム、コラボレーションの見方を分かりやすく解説し、Enclopediaでレンタルできるアーカイブへ案内します。",
      keyword:
        "初めてのジュンヤワタナベ,ジュンヤワタナベ 初めて,JUNYA WATANABE 初めて,ジュンヤワタナベ とは,JUNYA WATANABE どんなブランド,ジュンヤワタナベ レンタル,JUNYA WATANABE レンタル,ジュンヤワタナベマン レンタル,JUNYA WATANABE MAN レンタル,eYe JUNYA WATANABE MAN,ジュンヤワタナベ アーカイブ,JUNYA WATANABE アーカイブ,ジュンヤワタナベ コラボ,ジュンヤワタナベ パッチワーク,ジュンヤワタナベ デニム,enclopedia,アンクロペディア",
      canonicalPath: "/archive_guide/first_junya_watanabe_experience",
    },
    hero: {
      label: "FIRST JUNYA WATANABE EXPERIENCE",
      number: "05 / ENTRY",
      titleLines: ["初めての", "JUNYA WATANABEをレンタル体験。"],
      body:
        "JUNYA WATANABEは、\n見慣れた服をそのまま扱うのではなく、\n素材、パターン、機能、コラボレーションを通して別の服へ編集するブランドです。\n初めて知るなら、まずは“着ることで分かる構造の面白さ”から入るのが自然です。",
      keywords: [
        "first experience",
        "reconstruction",
        "patchwork",
        "junya watanabe",
      ],
      visualLabel: "ARCHIVE EXPERIENCE",
      visualTitle: ["服を作るのではなく、", "服の構造を編集する。"],
      image: "media/archive_fashion/archive_guide5.png",
      imageAlt:
        "初めてのJUNYA WATANABEをレンタル体験するためのアーカイブガイド画像",
      ctaLabel: "レンタルできるJUNYA WATANABEを見る",
      ctaHref:
        "https://www.enclopedia-official.com/brand/JUNYA%20WATANABE（ジュンヤワタナベ）/?number=32",
    },
    thesis: {
      text:
        "JUNYA WATANABEは、既存の服や素材を再編集することで、日常着の可能性を拡張するブランドです。",
      keywords: ["reconstruction", "collaboration", "experience"],
    },
    sections: {
      whyThisPage: {
        label: "02 / WHY THIS PAGE?",
        titleLines: [
          "JUNYA WATANABEが気になる。",
          "でも、どこから見ればいいか分からない。",
        ],
        body:
          "JUNYA WATANABEは、ファッション好きには強く評価される一方で、初めて見る人には“コラボが多いブランド”や“デザインが複雑なブランド”として見えることがあります。\nしかし本質は、単なる装飾や話題性ではありません。\nデニム、ワークウェア、ミリタリー、テーラード、スポーツウェアなど、すでに存在する服の構造を読み替え、別の表情へ変えるところに面白さがあります。",
      },
  
      brandOverview: {
        label: "03 / BRAND OVERVIEW",
        titleLines: [
          "服をゼロから作るのではなく、",
          "既存の服をもう一度読み替える。",
        ],
        body:
          "JUNYA WATANABEは、渡辺淳弥が手がけるブランドです。\nウィメンズのJUNYA WATANABE、メンズのJUNYA WATANABE MAN、より実用性と協業性が強く出るeYe JUNYA WATANABE MANなど、複数の入口があります。\n共通しているのは、既存の服の形、素材、機能を尊重しながら、それをそのまま終わらせないこと。パッチワーク、再構築、異素材の接続、コラボレーションを通じて、服の見え方を変えていきます。",
        knowledgeItems: [
          {
            title: "パッチワークは、装飾ではなく構造の編集。",
            text:
              "異なる布やパーツを組み合わせることで、服の表面だけでなく、成り立ちそのものを見せる。JUNYA WATANABEの代表的な読み方です。",
          },
          {
            title: "コラボレーションは、ロゴ交換ではない。",
            text:
              "Levi’s、THE NORTH FACE、Brooks Brothersなど、相手ブランドの持つ“本物の服”をどう再解釈するかが重要になります。",
          },
          {
            title: "デニムやワークウェアが、実験の土台になる。",
            text:
              "日常的で機能的な服を起点にするからこそ、再構築されたときの違いが分かりやすく立ち上がります。",
          },
          {
            title: "アーカイブで見ると、編集の蓄積が分かる。",
            text:
              "過去のシーズンを追うと、パターン、素材、コラボレーション、身体との距離がどのように変化してきたかが見えてきます。",
          },
        ],
      },
  
      lineGuide: {
        label: "04 / HOW TO SEE JUNYA?",
        titleLines: [
          "JUNYA WATANABEを見るなら、",
          "まず4つの視点から。",
        ],
        items: [
          {
            tag: "ENTRY 01",
            title: "RECONSTRUCTION",
            text:
              "既存の服を分解し、別のバランスで組み直す。JUNYA WATANABEを理解する最も基本的な視点です。",
          },
          {
            tag: "ENTRY 02",
            title: "PATCHWORK & MATERIAL",
            text:
              "デニム、チェック、ツイード、ナイロンなど、異なる素材を接続することで服に新しい構造を与えます。",
          },
          {
            tag: "ENTRY 03",
            title: "MAN / eYe",
            text:
              "JUNYA WATANABE MANやeYeでは、ワーク、ミリタリー、アウトドア、スポーツウェアの要素が強く現れます。",
          },
          {
            tag: "ENTRY 04",
            title: "COLLABORATION",
            text:
              "コラボレーションは話題性だけでなく、相手ブランドの服をJUNYAの設計思想へ翻訳する試みとして見られます。",
          },
        ],
      },
  
      firstExperience: {
        label: "05 / FIRST EXPERIENCE",
        titleLines: ["初めて着ると、", "何が分かるのか。"],
        intro:
          "JUNYA WATANABEの服は、写真で見るよりも、身体に乗せたときに“なぜこの構造なのか”が分かりやすくなるブランドです。",
        items: [
          {
            index: "01",
            title: "見慣れた服が、少し違う服に変わる",
            text:
              "ジャケット、デニム、ブルゾン、シャツ。知っている服の形を残しながら、切替や素材の接続によって別の存在感を持たせます。",
          },
          {
            index: "02",
            title: "素材のつながり方で、服の見え方が変わる",
            text:
              "パッチワークや異素材の切替は、表面的な装飾ではありません。服の重さ、動き、立体感を変える構造として働きます。",
          },
          {
            index: "03",
            title: "コラボレーションの意味が見えてくる",
            text:
              "相手ブランドの機能や歴史を残しながら、JUNYA WATANABEのパターンや編集で別の服へ変える。その変化は着ると理解しやすくなります。",
          },
        ],
      },
  
      quote: {
        textLines: [
          "既にある服を、",
          "もう一度発明する。",
        ],
      },
  
      whyRent: {
        label: "06 / WHY RENT?",
        titleLines: [
          "初めてのJUNYA WATANABEほど、",
          "レンタルで理解が深まる。",
        ],
        items: [
          {
            number: "01",
            title: "写真では、構造の厚みが読み切れない",
            text:
              "パッチワーク、切替、異素材の重なりは、写真だけでは分かりにくい部分があります。実物では、素材の厚みや立体感まで確認できます。",
          },
          {
            number: "02",
            title: "日常着との距離を確かめられる",
            text:
              "JUNYA WATANABEは、ワークウェアやデニムなど身近な服を土台にしているため、強いデザインでも日常に入る場合があります。",
          },
          {
            number: "03",
            title: "購入前に、コラボやアーカイブの価値を体験できる",
            text:
              "高額なコラボレーションや希少なアーカイブほど、見た目だけでなく、着たときの納得感を確かめる意味があります。",
          },
        ],
      },
  
      inventory: {
        label: "07 / AVAILABLE ARCHIVE",
        titleLines: ["Enclopediaで", "体験できるJUNYA WATANABE。"],
        body:
          "実在庫への導線は、“JUNYA WATANABEを知る続き”として設計します。まず背景を理解し、次に実物で確かめる流れを作ります。",
        items: [
          {
            meta: "JUNYA WATANABE MAN × FILSON / 2025-2026AW",
            title: "マッキーノ・クルーザージャケット",
            text:
              "クラシックなワークウェアを土台に、縮絨加工やポケット配置の編集で、定番服の見え方をずらした一着。",
            href:
              "https://www.enclopedia-official.com/product/448054dc-1314-4896-a70c-a0e1884fa137",
            image:
              "media/product/1775380069.160123448054dc-1314-4896-a70c-a0e1884fa137.png"
          },
          {
            meta: "JUNYA WATANABE MAN / 2025SS Dress up denim",
            title: "パッチワークテーラードジャケット",
            text:
              "デニムや異素材のパッチワークをさらに押し広げ、テーラードの端正さにパンクな編集感を重ねた2025SSらしい一着。",
            href:
              "https://www.enclopedia-official.com/product/d6b727ef-ac18-43d9-8323-0b8afc855db7",
            image:
              "media/product/1777819179.8824942d6b727ef-ac18-43d9-8323-0b8afc855db7.png"
          },
        ],
      },
  
      faq: {
        label: "08 / FAQ",
        titleLines: ["初めての人が、", "気になりやすいこと。"],
        items: [
          {
            question: "JUNYA WATANABEは、どんなブランドですか？",
            answer:
              "既存の服や素材を再編集し、パッチワーク、再構築、異素材切替、コラボレーションを通じて新しい服の見え方を作るブランドです。",
          },
          {
            question: "JUNYA WATANABEとJUNYA WATANABE MANは違いますか？",
            answer:
              "はい。JUNYA WATANABEはウィメンズを中心としたラインで、JUNYA WATANABE MANはメンズラインです。MANではワーク、ミリタリー、デニム、アウトドア、コラボレーションの文脈が強く出ます。",
          },
          {
            question: "初めてなら、どのアイテムから見るのがおすすめですか？",
            answer:
              "ジャケット、デニム、ブルゾン、シャツなど、日常着の形式を残しながら構造の違いが分かるアイテムがおすすめです。",
          },
          {
            question: "コラボレーションアイテムは初心者にも分かりやすいですか？",
            answer:
              "分かりやすいです。相手ブランドの定番服をJUNYA WATANABEがどう再編集しているかを見ると、ブランドの特徴を理解しやすくなります。",
          },
          {
            question: "レンタル後に購入もできますか？",
            answer:
              "対象アイテムは、レンタル体験を経たうえで購入相談へ進める導線を想定しています。",
          },
        ],
      },
  
      finalCta: {
        titleLines: [
          "初めてのJUNYA WATANABEを、",
          "レンタルから始める。",
        ],
        body:
          "既にある服が、構造と編集によって別の表情を持つ。\nEnclopediaで、JUNYA WATANABEのアーカイブを体験してください。",
        buttonLabel: "レンタルできるJUNYA WATANABEを見る",
        buttonHref:
          "https://www.enclopedia-official.com/brand/JUNYA%20WATANABE（ジュンヤワタナベ）/?number=32",
      },
    },
  },
  {
    slug: "first_noir_kei_ninomiya_experience",
    category: "BRAND ENTRY",
    title: "初めてのNOIR KEI NINOMIYAをレンタル体験",
    listDescription:
      "黒、チュール、ハーネス、構築的な造形。NOIR KEI NINOMIYAを“着ること”から理解する入口ページ。",
    seo: {
      title:
        "初めてのNOIR KEI NINOMIYAをレンタル体験｜ノワールケイニノミヤを着て知るEnclopedia",
      description:
        "NOIR KEI NINOMIYAが気になるけれど、どんなブランドか分からない方へ。ノワールケイニノミヤの魅力、チュール、ハーネス、フリル、レーザーカット、構築的ドレスの見方を分かりやすく解説し、Enclopediaでレンタルできるアーカイブへ案内します。",
      keyword:
        "初めてのNOIR KEI NINOMIYA,NOIR KEI NINOMIYA 初めて,ノワールケイニノミヤ 初めて,NOIR KEI NINOMIYA とは,ノワールケイニノミヤ どんなブランド,NOIR KEI NINOMIYA レンタル,ノワールケイニノミヤ レンタル,NOIR KEI NINOMIYA アーカイブ,ノワールケイニノミヤ アーカイブ,NOIR KEI NINOMIYA チュール,ノワールケイニノミヤ ハーネス,ノワールケイニノミヤ フリル,ノワールケイニノミヤ ドレス,enclopedia,アンクロペディア",
      canonicalPath: "/archive_guide/first_noir_kei_ninomiya_experience",
    },
    hero: {
      label: "FIRST NOIR KEI NINOMIYA EXPERIENCE",
      number: "06 / ENTRY",
      titleLines: ["初めての", "NOIR KEI NINOMIYAをレンタル体験。"],
      body:
        "NOIR KEI NINOMIYAは、\n黒を静かな色としてではなく、構造を浮かび上がらせるための色として扱うブランドです。\nチュール、ハーネス、フリル、パーツの接続。\n初めて知るなら、まずは“着ることで分かる黒の立体感”から入るのが自然です。",
      keywords: [
        "first experience",
        "black construction",
        "tulle",
        "noir kei ninomiya",
      ],
      visualLabel: "ARCHIVE EXPERIENCE",
      visualTitle: ["黒をまとうのではなく、", "黒の構造を着る。"],
      image: "media/archive_fashion/archive_guide6.png",
      imageAlt:
        "初めてのNOIR KEI NINOMIYAをレンタル体験するためのアーカイブガイド画像",
      ctaLabel: "レンタルできるNOIR KEI NINOMIYAを見る",
      ctaHref:
        "https://www.enclopedia-official.com/brand/NOIR%20KEI%20NINOMIYA（ノワールケイニノミヤ）/?number=50",
    },
    thesis: {
      text:
        "NOIR KEI NINOMIYAは、黒を装飾ではなく、素材・接続・造形によって立ち上げるブランドです。",
      keywords: ["black", "construction", "experience"],
    },
    sections: {
      whyThisPage: {
        label: "02 / WHY THIS PAGE?",
        titleLines: [
          "NOIR KEI NINOMIYAが気になる。",
          "でも、どこから見ればいいか分からない。",
        ],
        body:
          "NOIR KEI NINOMIYAは、写真では“黒くて装飾的な服”に見えることがあります。\nしかし実際には、チュールやフリルの繊細さ、ハーネスやパーツ接続の硬さ、身体から浮き上がるような立体構造が同時に存在しています。\nこのページでは、ノワールケイニノミヤを“可愛い黒”ではなく、“黒を構築するブランド”として理解する入口を作ります。",
      },
  
      brandOverview: {
        label: "03 / BRAND OVERVIEW",
        titleLines: [
          "黒をシンプルに見せるのではなく、",
          "黒で構造を浮かび上がらせる。",
        ],
        body:
          "NOIR KEI NINOMIYAは、コムデギャルソンの中でも、黒を軸に独自の造形を追求するブランドです。\nチュール、オーガンジー、フリル、ハーネス、スタッズ、リング、レーザーカット的な表現など、素材やパーツを組み合わせながら、服そのものを立体的な構造体として成立させます。\nそのため、ノワールケイニノミヤの魅力は、色や装飾だけではなく、“どう接続され、どう身体から立ち上がるか”にあります。",
        knowledgeItems: [
          {
            title: "チュールは、甘さではなく立体感を作る素材。",
            text:
              "NOIR KEI NINOMIYAのチュールは、柔らかい装飾だけではありません。硬さ、張り、重なりによって、黒の中に陰影と奥行きを生みます。",
          },
          {
            title: "ハーネスは、服と身体の距離を変える。",
            text:
              "身体に沿うだけではなく、身体の上にもう一つの構造を作る。ハーネスやパーツ接続は、服を立体的に見せる重要な要素です。",
          },
          {
            title: "装飾が、服の骨格になる。",
            text:
              "フリルやパーツは表面の飾りではなく、服の輪郭やボリュームを作る骨格として機能します。",
          },
          {
            title: "アーカイブで見ると、黒の表現の幅が分かる。",
            text:
              "同じ黒でも、シーズンや素材によって強さ、繊細さ、身体との距離が変わります。過去作を見ることで、その変化が分かります。",
          },
        ],
      },
  
      lineGuide: {
        label: "04 / HOW TO SEE NOIR?",
        titleLines: [
          "NOIR KEI NINOMIYAを見るなら、",
          "まず4つの視点から。",
        ],
        items: [
          {
            tag: "ENTRY 01",
            title: "BLACK",
            text:
              "黒を単色としてではなく、素材の厚み、透け、影、光の差で変化するものとして見る視点です。",
          },
          {
            tag: "ENTRY 02",
            title: "TULLE & FRILL",
            text:
              "チュールやフリルは甘さだけでなく、立体感と陰影を作る構造として機能します。",
          },
          {
            tag: "ENTRY 03",
            title: "HARNESS & PARTS",
            text:
              "ハーネス、スタッズ、リング、パーツ接続によって、身体の上にもう一つの骨格を作ります。",
          },
          {
            tag: "ENTRY 04",
            title: "SCULPTURAL FORM",
            text:
              "服を平面ではなく、身体から浮き上がる造形として見ると、NOIR KEI NINOMIYAの面白さが分かりやすくなります。",
          },
        ],
      },
  
      firstExperience: {
        label: "05 / FIRST EXPERIENCE",
        titleLines: ["初めて着ると、", "何が分かるのか。"],
        intro:
          "NOIR KEI NINOMIYAは、写真で見るよりも、実際に身体に乗せたときの奥行きや緊張感で理解が進むブランドです。",
        items: [
          {
            index: "01",
            title: "黒なのに、重く見えない理由",
            text:
              "透ける素材、重なり、空気を含むチュールによって、黒の中に軽さと陰影が生まれます。",
          },
          {
            index: "02",
            title: "装飾ではなく、構造として立ち上がる",
            text:
              "フリルやハーネスは表面の飾りではなく、服の輪郭を作る要素です。着ると身体との距離が見えてきます。",
          },
          {
            index: "03",
            title: "繊細さと強さが同時にある",
            text:
              "チュールの軽さと、パーツ接続の硬さ。相反する要素が同時にあることで、NOIR KEI NINOMIYAらしい緊張感が生まれます。",
          },
        ],
      },
  
      quote: {
        textLines: [
          "黒を着るのではなく、",
          "黒の構造を体験する。",
        ],
      },
  
      whyRent: {
        label: "06 / WHY RENT?",
        titleLines: [
          "初めてのNOIR KEI NINOMIYAほど、",
          "レンタルで理解が深まる。",
        ],
        items: [
          {
            number: "01",
            title: "写真では、素材の奥行きが読み切れない",
            text:
              "チュール、オーガンジー、フリル、パーツ接続は、平面写真では質感や空気感が伝わりにくい要素です。",
          },
          {
            number: "02",
            title: "身体との距離を確かめられる",
            text:
              "ハーネスや立体的なボリュームは、身体からどのように浮き上がるかで印象が変わります。実際に着る意味があります。",
          },
          {
            number: "03",
            title: "強いデザインを日常に入れられるか分かる",
            text:
              "視覚的には強く見えても、実際に着るとスタイリングに馴染むことがあります。レンタルはその距離を測る手段になります。",
          },
        ],
      },
  
      inventory: {
        label: "07 / AVAILABLE ARCHIVE",
        titleLines: ["Enclopediaで", "体験できるNOIR KEI NINOMIYA。"],
        body:
          "実在庫への導線は、“NOIR KEI NINOMIYAを知る続き”として設計します。まず背景を理解し、次に実物で確かめる流れを作ります。",
        items: [
          {
            meta: "NOIR KEI NINOMIYA / 2025SS Dark Rose",
            title: "デザインテーラードジャケット",
            text:
              "“ダークローズ”をテーマに、黒と赤、チュールやベルトの緊張感を重ねた2025SS。テーラードの輪郭にノワールらしい構築性を宿した一着。",
            href:
              "https://www.enclopedia-official.com/product/30dc8d80-a884-4515-b7fd-fa6a19c1e457",
            image:
              "media/product/1772806139.085684530dc8d80-a884-4515-b7fd-fa6a19c1e457.jpg",
          },
          {
            meta: "NOIR KEI NINOMIYA / 2023AW Noir in bloom",
            title: "サスペンダーボリュームスカート",
            text:
              "“Noir in bloom”の解放感を背景に、サスペンダーの硬さとボリュームのある造形を組み合わせた、花が咲くような立体感を持つスカート。",
            href:
              "https://www.enclopedia-official.com/product/b56062ca-e8ab-4f0e-a985-c8e74dd22cab",
            image:
              "media/product/1762346110.0680037b56062ca-e8ab-4f0e-a985-c8e74dd22cab.jpg",
          },
        ],
      },
  
      faq: {
        label: "08 / FAQ",
        titleLines: ["初めての人が、", "気になりやすいこと。"],
        items: [
          {
            question: "NOIR KEI NINOMIYAは、どんなブランドですか？",
            answer:
              "黒を軸に、チュール、ハーネス、フリル、パーツ接続などを用いて、服を立体的な構造として見せるブランドです。",
          },
          {
            question: "ノワールケイニノミヤは、コムデギャルソンのブランドですか？",
            answer:
              "はい。コムデギャルソンの中で展開されるブランドの一つで、二宮啓による黒を軸にした構築的な表現が特徴です。",
          },
          {
            question: "初めてなら、どのアイテムから見るのがおすすめですか？",
            answer:
              "ジャケット、チュールトップス、スカート、ハーネス系アイテムなど、黒の構造や素材の重なりが分かりやすいものがおすすめです。",
          },
          {
            question: "写真と実物で印象は変わりますか？",
            answer:
              "大きく変わります。特にチュールやフリルの奥行き、ハーネスの立体感、黒の陰影は実物で理解しやすい要素です。",
          },
          {
            question: "レンタル後に購入もできますか？",
            answer:
              "対象アイテムは、レンタル体験を経たうえで購入相談へ進める導線を想定しています。",
          },
        ],
      },
  
      finalCta: {
        titleLines: [
          "初めてのNOIR KEI NINOMIYAを、",
          "レンタルから始める。",
        ],
        body:
          "黒、チュール、ハーネス、構築的な造形。\nEnclopediaで、NOIR KEI NINOMIYAのアーカイブを体験してください。",
        buttonLabel: "レンタルできるNOIR KEI NINOMIYAを見る",
        buttonHref:
          "https://www.enclopedia-official.com/brand/NOIR%20KEI%20NINOMIYA（ノワールケイニノミヤ）/?number=50",
      },
    },
  },
  {
    slug: "first_raf_simons_experience",
    category: "BRAND ENTRY",
    title: "初めてのRAF SIMONSをレンタル体験",
    listDescription:
      "ユースカルチャー、音楽、グラフィック、オーバーサイズ。RAF SIMONSを“着ること”から理解する入口ページ。",
    seo: {
      title:
        "初めてのRAF SIMONSをレンタル体験｜ラフシモンズのアーカイブを着て知るEnclopedia",
      description:
        "RAF SIMONSが気になるけれど、どんなブランドか分からない方へ。ラフシモンズの魅力、ユースカルチャー、音楽、グラフィック、オーバーサイズ、Riot Riot RiotやCloserなどのアーカイブ文脈を分かりやすく解説し、EnclopediaでレンタルできるRAF SIMONSへ案内します。",
      keyword:
        "初めてのRAF SIMONS,RAF SIMONS 初めて,ラフシモンズ 初めて,RAF SIMONS とは,ラフシモンズ どんなブランド,RAF SIMONS レンタル,ラフシモンズ レンタル,RAF SIMONS アーカイブ,ラフシモンズ アーカイブ,RAF SIMONS 名作,ラフシモンズ 名作,Riot Riot Riot,Virginia Creeper,Consumed,Closer,ラフシモンズ オーバーサイズ,ラフシモンズ ボンバージャケット,enclopedia,アンクロペディア",
      canonicalPath: "/archive_guide/first_raf_simons_experience",
    },
    hero: {
      label: "FIRST RAF SIMONS EXPERIENCE",
      number: "07 / ENTRY",
      titleLines: ["初めての", "RAF SIMONSをレンタル体験。"],
      body:
        "RAF SIMONSは、\n服だけで完結するブランドではありません。\n音楽、アート、グラフィック、ユースカルチャー、そして若者の不安や反抗心までを服に落とし込んできたブランドです。\n初めて知るなら、まずは“着ることで分かる空気感”から入るのが自然です。",
      keywords: [
        "first experience",
        "youth culture",
        "archive",
        "raf simons",
      ],
      visualLabel: "ARCHIVE EXPERIENCE",
      visualTitle: ["服を着るというより、", "時代の空気をまとう。"],
      image: "media/archive_fashion/archive_guide7.png",
      imageAlt:
        "初めてのRAF SIMONSをレンタル体験するためのアーカイブガイド画像",
      ctaLabel: "レンタルできるRAF SIMONSを見る",
      ctaHref:
        "https://www.enclopedia-official.com/brand/RAF%20SIMONS%EF%BC%88%E3%83%A9%E3%83%95%E3%82%B7%E3%83%A2%E3%83%B3%E3%82%BA%EF%BC%89/?number=61",
    },
    thesis: {
      text:
        "RAF SIMONSは、若者の文化や感情を、テーラリングやグラフィック、シルエットによって服へ変換したブランドです。",
      keywords: ["youth", "music", "archive"],
    },
    sections: {
      whyThisPage: {
        label: "02 / WHY THIS PAGE?",
        titleLines: [
          "RAF SIMONSが気になる。",
          "でも、なぜアーカイブで評価されるのか分からない。",
        ],
        body:
          "RAF SIMONSは、古着市場やアーカイブシーンで強く評価されるブランドです。\nしかし初めて見る人にとっては、“高額なアーカイブ”“有名人が着ているブランド”“グラフィックが強い服”という印象に留まりやすい。\nこのページでは、ラフシモンズを単なるプレミア服ではなく、ユースカルチャー、音楽、アート、時代の空気を服にしたブランドとして整理します。",
      },
  
      brandOverview: {
        label: "03 / BRAND OVERVIEW",
        titleLines: [
          "若者の空気を、",
          "メンズウェアの形へ変えたブランド。",
        ],
        body:
          "RAF SIMONSは、ベルギー出身のデザイナー、ラフ・シモンズによるブランドです。\n1995年に本人名義ブランドを開始し、ユースカルチャー、音楽、アート、サブカルチャーを鋭いメンズウェアへ落とし込んできました。\n彼の服は、単に奇抜なデザインではなく、若者の孤独、反抗、集団性、憧れといった感情を、シルエットやグラフィック、素材感として表現してきた点に特徴があります。",
        knowledgeItems: [
          {
            title: "ユースカルチャーは、装飾ではなく主題。",
            text:
              "RAF SIMONSにおける若者文化は、単なる引用ではありません。音楽、学校、クラブ、反抗、孤独といった感覚が服の形やグラフィックに変換されています。",
          },
          {
            title: "オーバーサイズは、流行ではなく感情の輪郭。",
            text:
              "大きなニット、長い袖、余白のあるアウターは、身体を大きく見せるだけではありません。若者特有の不安定さや防御感を含んだシルエットとして読めます。",
          },
          {
            title: "グラフィックは、服の表面ではなくメッセージ。",
            text:
              "バンド、アート、写真、タイポグラフィ。RAF SIMONSのグラフィックは装飾ではなく、そのシーズンの思想やムードを強く伝える要素です。",
          },
          {
            title: "アーカイブで評価される理由。",
            text:
              "2000年代初期を中心とした作品は、単なる古着ではなく、当時のカルチャーとファッションの接点を示す資料として評価されています。",
          },
        ],
      },
  
      lineGuide: {
        label: "04 / HOW TO SEE RAF SIMONS?",
        titleLines: [
          "RAF SIMONSを見るなら、",
          "まず4つの視点から。",
        ],
        items: [
          {
            tag: "ENTRY 01",
            title: "YOUTH CULTURE",
            text:
              "若者の集団性、不安、反抗、孤独をどう服にしているか。RAF SIMONSを理解する最も重要な視点です。",
          },
          {
            tag: "ENTRY 02",
            title: "MUSIC & GRAPHIC",
            text:
              "Joy Divisionやニューウェーブ、パンク的なムードなど、音楽やグラフィックが服のメッセージとして機能します。",
          },
          {
            tag: "ENTRY 03",
            title: "OVERSIZED SILHOUETTE",
            text:
              "大きなニット、ボンバージャケット、細いパンツなど、身体との距離感がRAF SIMONSらしさを作ります。",
          },
          {
            tag: "ENTRY 04",
            title: "ARCHIVE SEASONS",
            text:
              "Riot Riot Riot、Virginia Creeper、Consumed、Closerなど、シーズン名ごとに語られる強い文脈があります。",
          },
        ],
      },
  
      firstExperience: {
        label: "05 / FIRST EXPERIENCE",
        titleLines: ["初めて着ると、", "何が分かるのか。"],
        intro:
          "RAF SIMONSは、写真で見るよりも、実際に着たときのサイズ感、重さ、グラフィックの距離で理解が深まるブランドです。",
        items: [
          {
            index: "01",
            title: "服というより、ムードを着る感覚がある",
            text:
              "ラフシモンズの服は、形だけでなく空気感が強い。音楽、青春、反抗、静けさが、シルエットやプリントから立ち上がります。",
          },
          {
            index: "02",
            title: "オーバーサイズの意味が分かる",
            text:
              "単に大きい服ではなく、身体を包む余白や袖の長さが、独特の孤独感や緊張感を作ります。着るとその距離感が見えてきます。",
          },
          {
            index: "03",
            title: "アーカイブの価格ではなく、残る理由が分かる",
            text:
              "なぜ古いRAF SIMONSが評価され続けるのか。着ることで、グラフィックやシーズン背景が単なるプレミアではないことが分かります。",
          },
        ],
      },
  
      quote: {
        textLines: [
          "若者の不安も、",
          "服のシルエットになる。",
        ],
      },
  
      whyRent: {
        label: "06 / WHY RENT?",
        titleLines: [
          "初めてのRAF SIMONSほど、",
          "レンタルで理解が深まる。",
        ],
        items: [
          {
            number: "01",
            title: "サイズ感を実際に確かめられる",
            text:
              "RAF SIMONSはシーズンによってシルエットの癖が強く、写真や寸法だけでは印象が読み切れない場合があります。",
          },
          {
            number: "02",
            title: "グラフィックの強さを体験できる",
            text:
              "プリントやメッセージ性のある服は、見るのと着るのでは印象が変わります。自分の生活に入るかを確かめられます。",
          },
          {
            number: "03",
            title: "高額アーカイブを所有前に理解できる",
            text:
              "RAF SIMONSのアーカイブは高額化しているものも多いため、まず体験として理解できることには大きな意味があります。",
          },
        ],
      },
  
      inventory: {
        label: "07 / AVAILABLE ARCHIVE",
        titleLines: ["Enclopediaで", "体験できるRAF SIMONS。"],
        body:
          "実在庫への導線は、“RAF SIMONSを知る続き”として設計します。まず背景を理解し、次に実物で確かめる流れを作ります。",
        items: [
          {
            meta: "RAF SIMONS / 2022SS",
            title: "テーラードブレザー",
            text:
              "端正なジャケットの形を残しながら、RAF SIMONSらしい緊張感とサイズバランスを体験できる一着。",
            href:
              "https://www.enclopedia-official.com/product/5d8729e6-36f6-429c-982b-6f3f4474b68a",
            image:
              "media/product/1776673803.38983445d8729e6-36f6-429c-982b-6f3f4474b68a.png"
          },
          {
            meta: "RAF SIMONS / 2022-2023AW",
            title: "ボンバージャケット",
            text:
              "西洋絵画的なイメージや重さのあるムードを、現代的なアウターとして着ることができるアーカイブ。",
            href:
              "https://www.enclopedia-official.com/product/c1306d6c-6029-4aaa-a316-9d8047c79695",
            image:
              "media/product/1771860586.6694794c1306d6c-6029-4aaa-a316-9d8047c79695.jpg"
          },
        ],
      },
  
      faq: {
        label: "08 / FAQ",
        titleLines: ["初めての人が、", "気になりやすいこと。"],
        items: [
          {
            question: "RAF SIMONSは、どんなブランドですか？",
            answer:
              "ベルギーのデザイナー、ラフ・シモンズによるブランドです。ユースカルチャー、音楽、アートを背景に、メンズウェアの見え方を大きく変えてきました。",
          },
          {
            question: "RAF SIMONSのアーカイブはなぜ評価されていますか？",
            answer:
              "特定の時代のユースカルチャーや音楽、グラフィック、シルエットが強く反映されているため、単なる古着ではなく文化的な資料としても評価されています。",
          },
          {
            question: "初めてなら、どのアイテムから見るのがおすすめですか？",
            answer:
              "ジャケット、ボンバージャケット、シャツ、ニットなど、日常的な形を持ちながらサイズ感やグラフィックに特徴があるアイテムがおすすめです。",
          },
          {
            question: "RAF SIMONSはレンタルで試す意味がありますか？",
            answer:
              "あります。サイズ感、グラフィックの強さ、アーカイブ特有のムードは、写真より実際に着ることで理解しやすくなります。",
          },
          {
            question: "レンタル後に購入もできますか？",
            answer:
              "対象アイテムは、レンタル体験を経たうえで購入相談へ進める導線を想定しています。",
          },
        ],
      },
  
      finalCta: {
        titleLines: [
          "初めてのRAF SIMONSを、",
          "レンタルから始める。",
        ],
        body:
          "ユースカルチャー、音楽、グラフィック、オーバーサイズ。\nEnclopediaで、RAF SIMONSのアーカイブを体験してください。",
        buttonLabel: "レンタルできるRAF SIMONSを見る",
        buttonHref:
          "https://www.enclopedia-official.com/brand/RAF%20SIMONS%EF%BC%88%E3%83%A9%E3%83%95%E3%82%B7%E3%83%A2%E3%83%B3%E3%82%BA%EF%BC%89/?number=61",
      },
    },
  },
  {
    slug: "first_rick_owens_experience",
    category: "BRAND ENTRY",
    title: "初めてのRICK OWENSをレンタル体験",
    listDescription:
      "退廃美、アシンメトリー、ドレープ、黒のシルエット。RICK OWENSを“着ること”から理解する入口ページ。",
    seo: {
      title:
        "初めてのRICK OWENSをレンタル体験｜リックオウエンスを着て知るEnclopedia",
      description:
        "RICK OWENSが気になるけれど、どんなブランドか分からない方へ。リックオウエンスの退廃美、アシンメトリー、ドレープ、レザー、黒・白・グレーのミニマルな色調、DRKSHDW、ジオバスケット、ラモーンズ、Opiumファッションとの関係まで分かりやすく解説し、Enclopediaでレンタルできるアーカイブへ案内します。",
      keyword:
        "初めてのRICK OWENS,RICK OWENS 初めて,リックオウエンス 初めて,RICK OWENS とは,リックオウエンス どんなブランド,RICK OWENS レンタル,リックオウエンス レンタル,RICK OWENS アーカイブ,リックオウエンス アーカイブ,RICK OWENS 着てみたい,リックオウエンス 着てみたい,RICK OWENS Opium,リックオウエンス Opiumファッション,Opiumファッション ブランド,Playboi Carti ファッション,RICK OWENS DRKSHDW,DRKSHDW レンタル,リックオウエンス ジオバスケット,リックオウエンス ラモーンズ,リックオウエンス サルエルパンツ,リックオウエンス レザージャケット,enclopedia,アンクロペディア",
      canonicalPath: "/archive_guide/first_rick_owens_experience",
    },
    hero: {
      label: "FIRST RICK OWENS EXPERIENCE",
      number: "08 / ENTRY",
      titleLines: ["初めての", "RICK OWENSをレンタル体験。"],
      body:
        "RICK OWENSは、\n黒い服やスニーカーのブランドとしてだけでは語れません。\n退廃的な美しさ、アシンメトリー、身体に沿うドレープ、影のような色調。\nグラマラスでありながらアンダーグラウンドでもある、その独自の空気を理解するには、まず実際に着てみるのが自然です。",
      keywords: [
        "first experience",
        "dark elegance",
        "asymmetry",
        "rick owens",
      ],
      visualLabel: "ARCHIVE EXPERIENCE",
      visualTitle: ["黒を着るのではなく、", "影の輪郭をまとう。"],
      image: "media/archive_fashion/archive_guide8.png",
      imageAlt:
        "初めてのRICK OWENSをレンタル体験するためのアーカイブガイド画像",
      ctaLabel: "レンタルできるRICK OWENSを見る",
      ctaHref:
        "https://www.enclopedia-official.com/brand/RICK%20OWENS%EF%BC%88%E3%83%AA%E3%83%83%E3%82%AF%E3%82%AA%E3%82%A6%E3%82%A8%E3%83%B3%E3%82%B9%EF%BC%89/?number=62",
    },
    thesis: {
      text:
        "RICK OWENSは、黒・ドレープ・レザー・アシンメトリーを通して、身体を整えるのではなく、退廃的で彫刻的な存在感へ変えるブランドです。",
      keywords: ["dark elegance", "drape", "asymmetry"],
    },
    sections: {
      whyThisPage: {
        label: "02 / WHY THIS PAGE?",
        titleLines: [
          "RICK OWENSが気になる。",
          "でも、どこから見ればいいか分からない。",
        ],
        body:
          "RICK OWENSは、強い見た目の服やスニーカーで知られる一方、初めて触れる人には“黒くて難しいブランド”に見えることがあります。\nしかし、その魅力は奇抜さだけではありません。\nグラマラスさとアンダーグラウンドを融合させた退廃美、身体に沿うようなドレープ、計算されたカッティング、くすんだシャドウカラー。\nそれらが重なることで、RICK OWENSは単なる黒い服ではなく、身体に別の輪郭を与える服になります。",
      },
  
      brandOverview: {
        label: "03 / BRAND OVERVIEW",
        titleLines: [
          "退廃的で、",
          "彫刻的な身体を作るブランド。",
        ],
        body:
          "RICK OWENSは、アメリカ出身のデザイナー、リック・オウエンスによるブランドです。\n黒、白、グレー、ダストカラーのようなくすんだ色調を軸にしながら、レザー、ドレープ、非対称なカッティング、縦に落ちるシルエットによって、独自の存在感を作ってきました。\nその服は、単に暗いわけでも、単にモードなわけでもありません。\nグラマラスでありながら地下的で、ミニマルでありながら異様。RICK OWENSの面白さは、その矛盾が一着の中で成立しているところにあります。",
        knowledgeItems: [
          {
            title: "退廃美は、汚さではなく緊張感。",
            text:
              "RICK OWENSの退廃的な美しさは、単にダメージ感や暗さを見せるものではありません。削ぎ落とされた色、長く落ちる布、硬質なレザーによって、静かな緊張感を作ります。",
          },
          {
            title: "アシンメトリーは、身体の見え方をずらす。",
            text:
              "左右非対称なカッティングや布の流れは、身体を整えるのではなく、あえて違和感のある輪郭を作ります。その歪みがRICK OWENSらしい存在感になります。",
          },
          {
            title: "ドレープは、身体に沿う影を作る。",
            text:
              "伝統的なオートクチュールを思わせる美しいドレープは、柔らかさだけでなく重力の表現でもあります。布がどこから落ち、どこに溜まるかで、身体の印象が変わります。",
          },
          {
            title: "Opiumファッションの文脈でも参照される。",
            text:
              "Playboi Carti周辺のOpiumファッションでは、黒、レザー、タイトなトップス、ボリュームのあるパンツ、退廃的なムードが重要になります。RICK OWENSはそのスタイルを象徴するブランドの一つとして語られやすい存在です。",
          },
        ],
      },
  
      lineGuide: {
        label: "04 / HOW TO SEE RICK OWENS?",
        titleLines: [
          "RICK OWENSを見るなら、",
          "まず4つの視点から。",
        ],
        items: [
          {
            tag: "ENTRY 01",
            title: "DECADENT ELEGANCE",
            text:
              "退廃的でありながら美しい。暗さ、レザー、ダメージ感、長いシルエットが、ただの粗さではなく洗練されたムードになります。",
          },
          {
            tag: "ENTRY 02",
            title: "ASYMMETRY & CUTTING",
            text:
              "左右非対称の構造、斜めに流れる布、身体に沿うカッティング。RICK OWENSの服は、身体の輪郭を少し異様に見せます。",
          },
          {
            tag: "ENTRY 03",
            title: "DRAPE & SHADOW COLOR",
            text:
              "黒、白、グレー、くすんだ色合い。ミニマルな色調の中で、ドレープや素材の影が服の表情を作ります。",
          },
          {
            tag: "ENTRY 04",
            title: "OPIUM / DRKSHDW / SNEAKERS",
            text:
              "DRKSHDW、ジオバスケット、ラモーンズ、Opiumファッションの文脈は、RICK OWENSを日常のスタイルへ接続する入口になります。",
          },
        ],
      },
  
      firstExperience: {
        label: "05 / FIRST EXPERIENCE",
        titleLines: ["初めて着ると、", "何が分かるのか。"],
        intro:
          "RICK OWENSは、写真で見るよりも、実際に着たときの布の重さ、丈の長さ、身体との距離で理解が深まるブランドです。",
        items: [
          {
            index: "01",
            title: "黒なのに、平坦に見えない理由",
            text:
              "同じ黒でも、レザー、コットン、ジャージー、加工された素材では影の出方が違います。着ると素材ごとの奥行きが分かります。",
          },
          {
            index: "02",
            title: "ドレープとカッティングが身体を変える",
            text:
              "身体に沿うように落ちる布、斜めに流れるライン、長い丈。RICK OWENSの服は、身体をきれいに整えるのではなく、異様で美しい存在感へ変えます。",
          },
          {
            index: "03",
            title: "Opium的なムードが、なぜ成立するのか分かる",
            text:
              "黒、レザー、細長いトップス、ボリュームのあるパンツ、退廃的な空気感。RICK OWENSを着ると、Opiumファッションが単なる流行ではなく、身体のシルエット作りでもあることが分かります。",
          },
        ],
      },
  
      quote: {
        textLines: [
          "暗さではなく、",
          "退廃的な美しさを着る。",
        ],
      },
  
      whyRent: {
        label: "06 / WHY RENT?",
        titleLines: [
          "初めてのRICK OWENSほど、",
          "レンタルで理解が深まる。",
        ],
        items: [
          {
            number: "01",
            title: "サイズ感と丈感を確かめられる",
            text:
              "RICK OWENSはアイテムによって丈、袖、股上、ボリュームの癖が強く、写真や寸法だけでは印象が読み切れません。",
          },
          {
            number: "02",
            title: "ドレープと素材の落ち方が分かる",
            text:
              "レザーや厚手素材、ドレープのある服は、実物で見たときに質感や重力のかかり方がはっきり分かります。",
          },
          {
            number: "03",
            title: "Opium的なスタイルを自分に落とし込めるか試せる",
            text:
              "黒、レザー、ボリュームパンツ、細長いシルエットは、見るのと着るのでは印象が変わります。レンタルは、その距離を測る手段になります。",
          },
        ],
      },
  
      inventory: {
        label: "07 / AVAILABLE ARCHIVE",
        titleLines: ["Enclopediaで", "体験できるRICK OWENS。"],
        body:
          "実在庫への導線は、“RICK OWENSを知る続き”として設計します。まず背景を理解し、次に実物で確かめる流れを作ります。",
        items: [
          {
            meta: "RICK OWENS / 2024SS Lido",
            title: "フォグポケットジャケット",
            text:
              "2024SS “Lido”の、煙や光をまとったような退廃的で彫刻的なムードを背景にした一着。ポケットの存在感と縦に落ちるシルエットで、RICK OWENSらしい影の輪郭を体験できます。",
            href:
              "https://www.enclopedia-official.com/product/e0429065-ee65-4314-b044-f436ed8fd02e",
            image:
              "media/product/1758033863.7903013e0429065-ee65-4314-b044-f436ed8fd02e.jpg",
          },
          {
            meta: "RICK OWENS DRKSHDW / 2023SS",
            title: "JUMBO WORKER JKT",
            text:
              "DRKSHDWらしい日常性とワークウェアの骨格を、ジャンボシルエットでRICK OWENSのバランスへ引き上げたジャケット。ストリートに取り入れやすく、ブランドのムードを体験しやすい一着です。",
            href:
              "https://www.enclopedia-official.com/product/46717e68-e9fa-4899-b9e0-72b228b6bb52",
            image:
              "media/product/1771165505.634241346717e68-e9fa-4899-b9e0-72b228b6bb52.jpg",
          },
        ],
      },
  
      faq: {
        label: "08 / FAQ",
        titleLines: ["初めての人が、", "気になりやすいこと。"],
        items: [
          {
            question: "RICK OWENSは、どんなブランドですか？",
            answer:
              "黒、ドレープ、レザー、アシンメトリーなカッティングを通じて、身体に退廃的で彫刻的な存在感を与えるブランドです。グラマラスさとアンダーグラウンドなムードが同居しています。",
          },
          {
            question: "RICK OWENSはOpiumファッションと関係がありますか？",
            answer:
              "公式に同じものではありませんが、Playboi Carti周辺のOpiumファッションでは、黒、レザー、細長いトップス、ボリュームパンツ、退廃的なムードが重視され、RICK OWENSはその代表的な参照ブランドの一つとして語られやすい存在です。",
          },
          {
            question: "RICK OWENSとDRKSHDWは違いますか？",
            answer:
              "DRKSHDWはRICK OWENSの中でも、より日常的でストリート寄りに取り入れやすいラインとして理解できます。スニーカーやパンツから入る人も多いです。",
          },
          {
            question: "初めてなら、どのアイテムから見るのがおすすめですか？",
            answer:
              "スニーカー、パンツ、カットソー、ドレープのあるトップスなど、日常着に近い形を持ちながらブランドの雰囲気が分かるアイテムがおすすめです。",
          },
          {
            question: "写真と実物で印象は変わりますか？",
            answer:
              "変わります。丈感、布の重さ、レザーの質感、身体との距離は実物でないと理解しづらい要素です。",
          },
          {
            question: "レンタル後に購入もできますか？",
            answer:
              "対象アイテムは、レンタル体験を経たうえで購入相談へ進める導線を想定しています。",
          },
        ],
      },
  
      finalCta: {
        titleLines: [
          "初めてのRICK OWENSを、",
          "レンタルから始める。",
        ],
        body:
          "退廃美、アシンメトリー、ドレープ、レザー、影のあるシルエット。\nEnclopediaで、RICK OWENSのアーカイブを体験してください。",
        buttonLabel: "レンタルできるRICK OWENSを見る",
        buttonHref:
          "https://www.enclopedia-official.com/brand/RICK%20OWENS%EF%BC%88%E3%83%AA%E3%83%83%E3%82%AF%E3%82%AA%E3%82%A6%E3%82%A8%E3%83%B3%E3%82%B9%EF%BC%89/?number=62",
      },
    },
  },
  {
    slug: "first_issey_miyake_experience",
    category: "BRAND ENTRY",
    title: "初めてのISSEY MIYAKEをレンタル体験",
    listDescription:
      "一枚の布、プリーツ、身体と空間。ISSEY MIYAKEを“着ること”から理解する入口ページ。",
    seo: {
      title:
        "初めてのISSEY MIYAKEをレンタル体験｜イッセイミヤケを着て知るEnclopedia",
      description:
        "ISSEY MIYAKEが気になるけれど、どんなブランドか分からない方へ。三宅一生の思想、生い立ち、プリーツ、一枚の布、A-POC、PLEATS PLEASE、HOMME PLISSÉなどの魅力を分かりやすく解説し、Enclopediaでレンタルできるアーカイブへ案内します。",
      keyword:
        "初めてのISSEY MIYAKE,ISSEY MIYAKE 初めて,イッセイミヤケ 初めて,ISSEY MIYAKE とは,イッセイミヤケ どんなブランド,ISSEY MIYAKE レンタル,イッセイミヤケ レンタル,ISSEY MIYAKE アーカイブ,イッセイミヤケ アーカイブ,PLEATS PLEASE レンタル,HOMME PLISSE レンタル,A-POC,イッセイミヤケ プリーツ,一枚の布,三宅一生 デザイン,enclopedia,アンクロペディア",
      canonicalPath: "/archive_guide/first_issey_miyake_experience",
    },
    hero: {
      label: "FIRST ISSEY MIYAKE EXPERIENCE",
      number: "09 / ENTRY",
      titleLines: ["初めての", "ISSEY MIYAKEをレンタル体験。"],
      body:
        "ISSEY MIYAKEは、\n“服を美しく見せるブランド”というより、\n布、身体、動き、技術の関係そのものを考え続けてきたブランドです。\nプリーツ、一枚の布、A-POC、身体を締めつけない構造。\n初めて知るなら、まずは“着ることで分かる自由さ”から入るのが自然です。",
      keywords: [
        "first experience",
        "pleats",
        "a piece of cloth",
        "issey miyake",
      ],
      visualLabel: "ARCHIVE EXPERIENCE",
      visualTitle: ["服を着るのではなく、", "布と身体の関係を体験する。"],
      image: "media/archive_fashion/archive_guide9.png",
      imageAlt:
        "初めてのISSEY MIYAKEをレンタル体験するためのアーカイブガイド画像",
      ctaLabel: "レンタルできるISSEY MIYAKEを見る",
      ctaHref:
        "https://www.enclopedia-official.com/brand/ISSEY%20MIYAKE（イッセイミヤケ）/?number=29",
    },
    thesis: {
      text:
        "ISSEY MIYAKEは、布を身体に従わせるのではなく、布と身体が一緒に動くための服を考え続けたブランドです。",
      keywords: ["cloth", "movement", "technology"],
    },
    sections: {
      whyThisPage: {
        label: "02 / WHY THIS PAGE?",
        titleLines: [
          "ISSEY MIYAKEが気になる。",
          "でも、どこから見ればいいか分からない。",
        ],
        body:
          "ISSEY MIYAKEは、プリーツの服やBAO BAOのバッグで知られています。\nしかし本質は、見た目の分かりやすいアイコンだけではありません。\n三宅一生が考え続けたのは、服を“流行の形”として作ることではなく、人が自由に動き、使い、生活できるデザインとして成立させることでした。\nこのページでは、ISSEY MIYAKEを“プリーツが有名なブランド”ではなく、“布と身体の関係を発明し続けたブランド”として整理します。",
      },
  
      brandOverview: {
        label: "03 / BRAND OVERVIEW",
        titleLines: [
          "生い立ちから生まれた、",
          "人のためのデザイン。",
        ],
        body:
          "三宅一生は広島に生まれ、若い頃にイサム・ノグチが設計した橋に触れた経験から、デザインは人に見られるだけでなく、人に使われ、生活の中で力を持つものだと考えるようになります。\nその視点は、後の服作りにも深くつながっています。\n身体を無理に締めつけるのではなく、布が身体に沿い、動き、空気を含み、日常の中で使われること。\nISSEY MIYAKEの服は、彫刻のようでありながら、同時にとても実用的です。",
        knowledgeItems: [
          {
            title: "広島での経験は、“希望としてのデザイン”へつながる。",
            text:
              "三宅一生のデザインには、破壊や喪失の記憶を単に語るのではなく、そこから人が生きるための形を作るという姿勢があります。服は飾りではなく、人を前に進ませる道具になります。",
          },
          {
            title: "一枚の布は、服の原点を問い直す考え方。",
            text:
              "身体を型にはめるのではなく、一枚の布が身体の上でどう動き、どう形になるかを考える。ここにISSEY MIYAKEの根本的な面白さがあります。",
          },
          {
            title: "プリーツは、装飾ではなく機能。",
            text:
              "プリーツは見た目の表情だけでなく、伸縮性、軽さ、動きやすさ、畳みやすさを生む技術です。服をより自由にするための構造として機能します。",
          },
          {
            title: "テクノロジーと手仕事が同居する。",
            text:
              "ISSEY MIYAKEは、工業技術や新素材を使いながら、人間の身体や生活の感覚を失わないブランドです。未来的でありながら、どこか人間的です。",
          },
        ],
      },
  
      lineGuide: {
        label: "04 / HOW TO SEE ISSEY MIYAKE?",
        titleLines: [
          "ISSEY MIYAKEを見るなら、",
          "まず4つの視点から。",
        ],
        items: [
          {
            tag: "ENTRY 01",
            title: "A PIECE OF CLOTH",
            text:
              "一枚の布が身体の上でどう変化するか。ISSEY MIYAKEを理解する最も根本的な視点です。",
          },
          {
            tag: "ENTRY 02",
            title: "PLEATS",
            text:
              "プリーツは装飾ではなく、動きやすさ、軽さ、携帯性、立体感を同時に生む技術として見ると面白くなります。",
          },
          {
            tag: "ENTRY 03",
            title: "BODY & MOVEMENT",
            text:
              "服が身体を固定するのではなく、身体の動きに反応する。歩く、座る、腕を上げる。そのたびに服の形が変わります。",
          },
          {
            tag: "ENTRY 04",
            title: "TECHNOLOGY & DAILY LIFE",
            text:
              "A-POCやHOMME PLISSÉのように、技術が実験で終わらず、日常で着られる服へ落とし込まれる点が重要です。",
          },
        ],
      },
  
      firstExperience: {
        label: "05 / FIRST EXPERIENCE",
        titleLines: ["初めて着ると、", "何が分かるのか。"],
        intro:
          "ISSEY MIYAKEの服は、ハンガーに掛かっている状態と、身体に乗せた状態で印象が大きく変わります。",
        items: [
          {
            index: "01",
            title: "軽さが、見た目以上に身体を変える",
            text:
              "プリーツや薄い素材は、服を着ている重さを忘れさせます。身体を締めつけず、動きに合わせて形が変わるため、着た瞬間に自由さが分かります。",
          },
          {
            index: "02",
            title: "平面と立体の行き来が面白い",
            text:
              "畳まれていると平面的なのに、着ると立体になる。ISSEY MIYAKEの服は、布が身体によって完成する感覚があります。",
          },
          {
            index: "03",
            title: "“楽な服”なのに、緊張感がある",
            text:
              "着心地は楽なのに、見た目には彫刻的な強さがある。この矛盾がISSEY MIYAKEの魅力です。日常着と実験服が同時に成立しています。",
          },
        ],
      },
  
      quote: {
        textLines: [
          "布は、身体に従うだけではない。",
          "身体と一緒に動き、形を作る。",
        ],
      },
  
      whyRent: {
        label: "06 / WHY RENT?",
        titleLines: [
          "初めてのISSEY MIYAKEほど、",
          "レンタルで理解が深まる。",
        ],
        items: [
          {
            number: "01",
            title: "写真では、軽さと動きが伝わりきらない",
            text:
              "プリーツの伸縮、布の揺れ、身体に沿う感覚は、写真では分かりにくい部分です。実際に着ることで初めて理解できます。",
          },
          {
            number: "02",
            title: "自分の身体との相性を確かめられる",
            text:
              "ISSEY MIYAKEの服は、体型を隠すのではなく、身体の動きに反応します。サイズ表だけでは分からない相性を体験できます。",
          },
          {
            number: "03",
            title: "ブランドの思想を、生活の中で試せる",
            text:
              "美術館で見るような服ではなく、実際に歩き、座り、動くことで意味が分かる服です。レンタルはその思想に触れる最短の入口になります。",
          },
        ],
      },
  
      inventory: {
        label: "07 / AVAILABLE ARCHIVE",
        titleLines: ["Enclopediaで", "体験できるISSEY MIYAKE。"],
        body:
          "実在庫への導線は、“ISSEY MIYAKEを知る続き”として設計します。まず背景を理解し、次に実物で確かめる流れを作ります。",
        items: [
          {
            meta: "ISSEY MIYAKE MEN / 2019AW Wind",
            title: "テーラードジャケット",
            text:
              "2019AWのキーワードである“風”を背景に、軽さや動き、空気を含むような着用感をテーラードの形に落とし込んだ一着。",
            href:
              "https://www.enclopedia-official.com/product/c8c69666-09c6-4717-b1f7-19efbbf0a11c",
            image:
              "media/product/1779192290.4588382c8c69666-09c6-4717-b1f7-19efbbf0a11c.png"
          },
          {
            meta: "ISSEY MIYAKE / 2024SS Grasping the Formless",
            title: "ノーカラージャケット",
            text:
              "“見えない形が見えるまで”をテーマに、風や光のように輪郭のないものを服へ写した2024SS。余白のある形と身体への自然な呼応を感じられるジャケット。",
            href:
              "https://www.enclopedia-official.com/product/a1acaa9e-af5a-486c-96c8-c4b8a4e5d37c",
            image:
              "media/product/1755089006.0275738a1acaa9e-af5a-486c-96c8-c4b8a4e5d37c.jpg",
          },
          {
            meta: "PLEATS PLEASE ISSEY MIYAKE / 2010AW",
            title: "マルチカラープリーツワンピース",
            text:
              "軽く、畳めて、動きに強いPLEATS PLEASEの特性を、マルチカラーの表情で楽しめるワンピース。身体の動きに合わせて色とプリーツが変化します。",
            href:
              "https://www.enclopedia-official.com/product/a4f03d17-f41f-4324-b74b-463b88f77525",
            image:
              "media/product/1768315424.469848a4f03d17-f41f-4324-b74b-463b88f77525.jpg",
          },
        ],
      },
  
      faq: {
        label: "08 / FAQ",
        titleLines: ["初めての人が、", "気になりやすいこと。"],
        items: [
          {
            question: "ISSEY MIYAKEは、どんなブランドですか？",
            answer:
              "布、身体、動き、技術の関係を考え続けてきたブランドです。プリーツやA-POCなど、服作りそのものを更新する発想が特徴です。",
          },
          {
            question: "ISSEY MIYAKEとPLEATS PLEASEは違いますか？",
            answer:
              "はい。ISSEY MIYAKEはブランド全体の中核で、PLEATS PLEASEはプリーツ技術を日常着として展開する代表的なラインです。",
          },
          {
            question: "HOMME PLISSÉ ISSEY MIYAKEはどんなラインですか？",
            answer:
              "メンズ向けに発展したプリーツのラインです。動きやすさ、軽さ、扱いやすさがありながら、独特の立体感があります。",
          },
          {
            question: "初めてなら、どのアイテムから見るのがおすすめですか？",
            answer:
              "プリーツパンツ、ジャケット、トップスなど、日常的に着やすく、動いたときの変化が分かるアイテムがおすすめです。",
          },
          {
            question: "写真と実物で印象は変わりますか？",
            answer:
              "大きく変わります。プリーツの伸縮、布の軽さ、動いたときの揺れは、実物で着ると最も分かりやすい要素です。",
          },
          {
            question: "レンタル後に購入もできますか？",
            answer:
              "対象アイテムは、レンタル体験を経たうえで購入相談へ進める導線を想定しています。",
          },
        ],
      },
  
      finalCta: {
        titleLines: [
          "初めてのISSEY MIYAKEを、",
          "レンタルから始める。",
        ],
        body:
          "一枚の布、プリーツ、身体の動き、技術の美しさ。\nEnclopediaで、ISSEY MIYAKEのアーカイブを体験してください。",
        buttonLabel: "レンタルできるISSEY MIYAKEを見る",
        buttonHref:
          "https://www.enclopedia-official.com/brand/ISSEY%20MIYAKE（イッセイミヤケ）/?number=29",
      },
    },
  },
  {
    slug: "first_yohji_yamamoto_experience",
    category: "BRAND ENTRY",
    title: "初めてのYOHJI YAMAMOTOをレンタル体験",
    listDescription:
      "黒、余白、反骨、ドレープ。YOHJI YAMAMOTOを“着ること”から理解する入口ページ。",
    seo: {
      title:
        "初めてのYOHJI YAMAMOTOをレンタル体験｜ヨウジヤマモトを着て知るEnclopedia",
      description:
        "YOHJI YAMAMOTOが気になるけれど、どんなブランドか分からない方へ。山本耀司の生い立ち、黒、ドレープ、オーバーサイズ、Yohji Yamamoto POUR HOMME、Y's、Ground Y、北野武映画『BROTHER』の衣装文脈まで分かりやすく解説し、Enclopediaでレンタルできるアーカイブへ案内します。",
      keyword:
        "初めてのYOHJI YAMAMOTO,YOHJI YAMAMOTO 初めて,ヨウジヤマモト 初めて,YOHJI YAMAMOTO とは,ヨウジヤマモト どんなブランド,YOHJI YAMAMOTO レンタル,ヨウジヤマモト レンタル,YOHJI YAMAMOTO アーカイブ,ヨウジヤマモト アーカイブ,Yohji Yamamoto POUR HOMME,ヨウジヤマモトプールオム,Y's レンタル,Ground Y レンタル,ヨウジヤマモト 黒,ヨウジヤマモト ドレープ,ヨウジヤマモト 北野武,BROTHER 衣装 ヨウジヤマモト,ヤクザスーツ,enclopedia,アンクロペディア",
      canonicalPath: "/archive_guide/first_yohji_yamamoto_experience",
    },
    hero: {
      label: "FIRST YOHJI YAMAMOTO EXPERIENCE",
      number: "10 / ENTRY",
      titleLines: ["初めての", "YOHJI YAMAMOTOをレンタル体験。"],
      body:
        "YOHJI YAMAMOTOは、\n黒い服のブランドとしてだけでは語れません。\n余白、ドレープ、身体を締めつけない分量、そして社会の美意識に対する静かな反抗。\n初めて知るなら、まずは“着ることで分かる黒の色気と余白”から入るのが自然です。",
      keywords: [
        "first experience",
        "black",
        "drape",
        "yohji yamamoto",
      ],
      visualLabel: "ARCHIVE EXPERIENCE",
      visualTitle: ["黒を着るのではなく、", "余白と反骨をまとう。"],
      image: "media/archive_fashion/archive_guide10.png",
      imageAlt:
        "初めてのYOHJI YAMAMOTOをレンタル体験するためのアーカイブガイド画像",
      ctaLabel: "レンタルできるYOHJI YAMAMOTOを見る",
      ctaHref:
        "https://www.enclopedia-official.com/brand/YOHJI%20YAMAMOTO（ヨウジヤマモト）/?number=71",
    },
    thesis: {
      text:
        "YOHJI YAMAMOTOは、身体を飾るのではなく、身体を守り、隠し、余白によって存在感を立ち上げるブランドです。",
      keywords: ["black", "space", "rebellion"],
    },
    sections: {
      whyThisPage: {
        label: "02 / WHY THIS PAGE?",
        titleLines: [
          "YOHJI YAMAMOTOが気になる。",
          "でも、黒い服の何が特別なのか分からない。",
        ],
        body:
          "YOHJI YAMAMOTOは、“黒くて大きい服”という印象で語られがちです。\nしかし、その黒は単なる色ではありません。\n身体を隠すための黒、社会の美意識に抵抗する黒、動いたときに影を生む黒。\nこのページでは、ヨウジヤマモトを“雰囲気のあるモード服”ではなく、山本耀司の生い立ち、反骨性、映画衣装の色気まで含めて、着て理解するためのブランドとして整理します。",
      },
  
      brandOverview: {
        label: "03 / BRAND OVERVIEW",
        titleLines: [
          "父を失った戦後の記憶と、",
          "母の洋裁店から生まれた服。",
        ],
        body:
          "山本耀司は1943年、東京に生まれました。\n父を戦争で亡くし、戦後に働く母の姿を見て育ったことは、彼の服作りを語る上で欠かせない背景です。\n慶應義塾大学で法律を学んだ後、母の洋裁店を手伝い、さらに文化服装学院で服作りを学びます。\nその出発点には、流行を作ることよりも、身体を守ること、既存の女性らしさ・男性らしさに抗うこと、そして人間の弱さや強さを服で受け止める感覚があります。",
        knowledgeItems: [
          {
            title: "黒は、悲しみではなく意志の色。",
            text:
              "ヨウジの黒は、無難な黒ではありません。社会が求める華やかさや分かりやすい美しさに対して、黙って距離を取るための色です。",
          },
          {
            title: "大きな服は、身体を隠すためだけではない。",
            text:
              "オーバーサイズのジャケットやコートは、身体を覆いながら、同時に着る人の存在感を大きく見せます。守ることと強く見せることが同時に起きます。",
          },
          {
            title: "ドレープは、布が人間に寄り添う線。",
            text:
              "身体にぴったり沿わせるのではなく、布が落ち、揺れ、余る。そこに人間の弱さ、色気、動きが出ます。",
          },
          {
            title: "北野武映画のスーツは、ヨウジの色気が分かる入口。",
            text:
              "映画『BROTHER』のヤクザスーツは、単に怖い衣装ではありません。黒いスーツ、余った布、肩の強さ、静かな緊張感が、暴力よりも色気として残ります。",
          },
        ],
      },
  
      lineGuide: {
        label: "04 / HOW TO SEE YOHJI?",
        titleLines: [
          "YOHJI YAMAMOTOを見るなら、",
          "まず4つの視点から。",
        ],
        items: [
          {
            tag: "ENTRY 01",
            title: "BLACK",
            text:
              "黒を単なる色ではなく、陰影、反骨、距離感として見る。ヨウジヤマモトを理解する最初の視点です。",
          },
          {
            tag: "ENTRY 02",
            title: "DRAPE & SPACE",
            text:
              "身体に密着させるのではなく、布と身体の間に余白を作る。そこに動きと色気が生まれます。",
          },
          {
            tag: "ENTRY 03",
            title: "POUR HOMME / Y's",
            text:
              "POUR HOMMEはメンズの強い造形、Y'sは日常に近い実用性と反骨性が魅力。入口の違いを知ると選びやすくなります。",
          },
          {
            tag: "ENTRY 04",
            title: "CINEMA & SUITS",
            text:
              "北野武映画『BROTHER』の衣装のように、ヨウジのスーツは物語の中で人間の危うさと色気を強く引き出します。",
          },
        ],
      },
  
      firstExperience: {
        label: "05 / FIRST EXPERIENCE",
        titleLines: ["初めて着ると、", "何が分かるのか。"],
        intro:
          "YOHJI YAMAMOTOは、写真で見るよりも、実際に着たときの布の落ち方、黒の奥行き、身体との距離で理解が深まるブランドです。",
        items: [
          {
            index: "01",
            title: "黒なのに、重く見えすぎない理由",
            text:
              "同じ黒でも、ウールギャバジン、レーヨン、コットン、リネンでは光の拾い方が違います。実物では、黒の中にある陰影の幅が見えます。",
          },
          {
            index: "02",
            title: "大きい服なのに、だらしなく見えない理由",
            text:
              "ヨウジのオーバーサイズは、ただサイズが大きいだけではありません。肩、背中、裾、布の落ち方が計算されているため、余白が緊張感に変わります。",
          },
          {
            index: "03",
            title: "スーツが、社会性ではなく色気になる",
            text:
              "普通のスーツは整えるための服です。ヨウジのスーツは、少し崩れ、布が余り、影を作ることで、着る人の危うさや色気を引き出します。",
          },
        ],
      },
  
      quote: {
        textLines: [
          "黒は隠すための色であり、",
          "存在を強くするための色でもある。",
        ],
      },
  
      whyRent: {
        label: "06 / WHY RENT?",
        titleLines: [
          "初めてのYOHJI YAMAMOTOほど、",
          "レンタルで理解が深まる。",
        ],
        items: [
          {
            number: "01",
            title: "写真では、黒の質感が伝わりきらない",
            text:
              "黒い服は写真で差が出にくいですが、実物では素材の厚み、光沢、落ち感、陰影が大きく違います。",
          },
          {
            number: "02",
            title: "サイズ感と余白を確かめられる",
            text:
              "ヨウジヤマモトは分量が大きい服が多く、サイズ表だけでは判断しにくいブランドです。着ることで余白の意味が分かります。",
          },
          {
            number: "03",
            title: "“似合うか不安”を体験に変えられる",
            text:
              "黒、ドレープ、オーバーサイズは強く見えますが、実際に着ると身体を受け止めてくれる感覚があります。レンタルはその距離を縮める入口になります。",
          },
        ],
      },
  
      inventory: {
        label: "07 / AVAILABLE ARCHIVE",
        titleLines: ["Enclopediaで", "体験できるYOHJI YAMAMOTO。"],
        body:
          "実在庫への導線は、“YOHJI YAMAMOTOを知る続き”として設計します。まず背景を理解し、次に実物で確かめる流れを作ります。",
        items: [
          {
            meta: "Y's for men / 2023AW",
            title: "テーラードジャケット",
            text:
              "再始動したY's for menの文脈を持つ2023AW。Yohjiらしい黒の余白と実用服の落ち着きを、端正なテーラードの形で体験できる一着。",
            href:
              "https://www.enclopedia-official.com/product/b267d829-0236-4588-9bbf-ed5f3f376fba",
            image:
              "media/product/1762345150.0142834b267d829-0236-4588-9bbf-ed5f3f376fba.jpg",
          },
          {
            meta: "YOHJI YAMAMOTO POUR HOMME / 2022SS",
            title: "釈迦釦 長袖シャツ",
            text:
              "2022SSの、光と影、過去のアーカイブ、シュルレアリスムやワークウェアの要素が交差する文脈を背景にしたシャツ。釈迦釦の存在感が、黒の静けさに象徴性を加えます。",
            href:
              "https://www.enclopedia-official.com/product/7f645c4b-4726-40e7-9cc0-cb8f2c4058be",
            image:
              "media/product/1756906703.2119977f645c4b-4726-40e7-9cc0-cb8f2c4058be.jpg",
          },
        ],
      },
  
      faq: {
        label: "08 / FAQ",
        titleLines: ["初めての人が、", "気になりやすいこと。"],
        items: [
          {
            question: "YOHJI YAMAMOTOは、どんなブランドですか？",
            answer:
              "黒、ドレープ、オーバーサイズ、非対称なカッティングを通じて、身体に余白と存在感を与えるブランドです。既存の美しさや性別の見え方に対する反骨性も特徴です。",
          },
          {
            question: "Yohji Yamamoto POUR HOMMEとY'sは違いますか？",
            answer:
              "はい。POUR HOMMEはメンズのコレクション性が強く、Y'sはより日常に近い実用性と反骨性を持つラインとして理解できます。",
          },
          {
            question: "北野武の映画『BROTHER』と関係がありますか？",
            answer:
              "はい。映画『BROTHER』では山本耀司が衣装デザインを手がけており、黒いスーツやヤクザスタイルの静かな色気を知る上で、ヨウジヤマモトの魅力が分かりやすい文脈です。",
          },
          {
            question: "初めてなら、どのアイテムから見るのがおすすめですか？",
            answer:
              "黒いジャケット、ワイドパンツ、ロングシャツ、ドレープのあるコートなど、ヨウジらしい余白と布の落ち方が分かるアイテムがおすすめです。",
          },
          {
            question: "写真と実物で印象は変わりますか？",
            answer:
              "大きく変わります。黒の質感、布の重さ、ドレープ、肩や裾の余白は、実物で着ると最も理解しやすい要素です。",
          },
          {
            question: "レンタル後に購入もできますか？",
            answer:
              "対象アイテムは、レンタル体験を経たうえで購入相談へ進める導線を想定しています。",
          },
        ],
      },
  
      finalCta: {
        titleLines: [
          "初めてのYOHJI YAMAMOTOを、",
          "レンタルから始める。",
        ],
        body:
          "黒、余白、ドレープ、反骨、そして映画のような色気。\nEnclopediaで、YOHJI YAMAMOTOのアーカイブを体験してください。",
        buttonLabel: "レンタルできるYOHJI YAMAMOTOを見る",
        buttonHref:
          "https://www.enclopedia-official.com/brand/YOHJI%20YAMAMOTO（ヨウジヤマモト）/?number=71",
      },
    },
  },
];

function Seo({ article, canonicalUrl }) {
  const title = article.seo.title;
  const description = article.seo.description;
  const keyword = article.seo.keyword;
  const ogImage = article.hero.image
    ? `${process.env.REACT_APP_IMAGE_URL || ""}${article.hero.image}`
    : `${process.env.PUBLIC_URL || ""}/favicon.ico`;

  useEffect(() => {
    headRewrite(title, description, keyword);
  }, [title, description, keyword]);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keyword} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:type" content="article" />
      <meta property="og:site_name" content="Enclopedia" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: title,
          description,
          mainEntityOfPage: canonicalUrl,
          publisher: {
            "@type": "Organization",
            name: "Enclopedia",
          },
          about: [
            "アーカイブファッション",
            "ブランドガイド",
            article.title,
          ],
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: typeof window !== "undefined" ? window.location.origin : "",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Archive Guide",
              item: `${typeof window !== "undefined" ? window.location.origin : ""}/archive_guide`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: article.title,
              item: canonicalUrl,
            },
          ],
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: article.sections.faq.items.map((item) => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: item.answer,
            },
          })),
        })}
      </script>
    </Helmet>
  );
}

function IndexSeo() {
  const title = "アーカイブガイド｜ブランドを深く知るEnclopedia";
  const description =
    "Enclopediaのアーカイブガイド一覧。ブランドの入口、初めての体験、アーカイブを楽しむための知識をまとめています。";
  const keyword =
    "アーカイブガイド,ブランドガイド,ファッションアーカイブ,コムデギャルソン 初めて,Enclopedia";
  const canonicalUrl = `${typeof window !== "undefined" ? window.location.origin : ""}/archive_guide`;

  useEffect(() => {
    headRewrite(title, description, keyword);
  }, [title, description, keyword]);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keyword} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Enclopedia" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: title,
          description,
          url: canonicalUrl,
          isPartOf: {
            "@type": "WebSite",
            name: "Enclopedia",
            url: typeof window !== "undefined" ? window.location.origin : "",
          },
        })}
      </script>
    </Helmet>
  );
}

function ArchiveGuideIndex({ classes }) {
  return (
    <div className={classes.root}>
      <IndexSeo />
      <Container maxWidth="xl">
        <div className={classes.pageFrame}>
          <section className={classes.indexHero}>
            <div className={classes.indexIntro}>
              <div className={classes.labelRow}>
                <span>ARCHIVE GUIDE</span>
                <span className={classes.labelLine} />
              </div>
              <Typography variant="h1" component="h1" className={classes.heroTitle}>
                ブランドを、
                <br />
                深く知る入口。
              </Typography>
              <Typography component="p" className={classes.heroBody}>
                Enclopediaのアーカイブガイドでは、ブランドの背景、初めて着ると見えてくること、アーカイブの楽しみ方を記事として整理します。
              </Typography>
            </div>
          </section>

          <section className={classes.section}>
            <div className={classes.guideGrid}>
              {guideArticles.map((article) => (
                <a
                  href={`/archive_guide/${article.slug}`}
                  className={classes.guideCard}
                  key={article.slug}
                >
                  <div>
                    <div className={classes.guideCategory}>{article.category}</div>
                    <Typography component="h2" className={classes.guideCardTitle}>
                      {article.title}
                    </Typography>
                    <Typography component="p" className={classes.guideCardBody}>
                      {article.listDescription}
                    </Typography>
                  </div>
                  <div className={classes.guideArrow}>記事を読む →</div>
                </a>
              ))}
            </div>
          </section>
        </div>
      </Container>
    </div>
  );
}

function ArchiveGuideNotFound({ classes }) {
  return (
    <div className={classes.root}>
      <Container maxWidth="xl">
        <div className={classes.notFound}>
          <div>
            <Typography component="h1" className={classes.heading}>
              記事が見つかりませんでした。
            </Typography>
            <Button className={classes.primaryButton} component="a" href="/archive_guide">
              アーカイブガイド一覧へ戻る
            </Button>
          </div>
        </div>
      </Container>
    </div>
  );
}

function ArchiveGuideDetail({ classes, article }) {
  const canonicalUrl = `${typeof window !== "undefined" ? window.location.origin : ""}${article.seo.canonicalPath}`;
  const { hero, thesis, sections } = article;

  return (
    <div className={classes.root}>
      <Seo article={article} canonicalUrl={canonicalUrl} />
      <Container maxWidth="xl">
        <div className={classes.pageFrame}>
          <section className={classes.hero}>
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={7}>
                <div className={classes.labelRow}>
                  <span>{hero.label}</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography variant="h1" component="h1" className={classes.heroTitle}>
                  {hero.titleLines.map((line, index) => (
                    <React.Fragment key={line}>
                      {line}
                      {index < hero.titleLines.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </Typography>
                <Typography component="p" className={classes.heroBody}>
                  {hero.body}
                </Typography>
                <div className={classes.buttonRow}>
                <Button
                  className={classes.primaryButton}
                  component="a"
                  href={hero.ctaHref || "https://www.enclopedia-official.com/"}
                >
                  {hero.ctaLabel || "レンタルできるアイテムを見る"}
                </Button>
                  <a href="#faq" className={classes.textLink}>
                    初めての人が気になりやすいこと
                  </a>
                </div>
                <div className={classes.keywordLine}>
                  {hero.keywords.map((keyword, index) => (
                    <React.Fragment key={keyword}>
                      <span>{keyword}</span>
                      {index < hero.keywords.length - 1 && <span>/</span>}
                    </React.Fragment>
                  ))}
                </div>
              </Grid>
              <Grid item xs={12} md={5}>
                <div className={classes.heroVisual}>
                  <div className={classes.heroCircle} />
                  <div className={classes.heroSquare} />
                  <div className={classes.heroNumber}>{hero.number}</div>
                  <div className={classes.heroMock}>
                    <img
                      src={`${process.env.REACT_APP_IMAGE_URL || ""}${hero.image}`}
                      alt={hero.imageAlt}
                      className={classes.heroMockImage}
                    />
                  </div>
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <Grid container spacing={6} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <div className={classes.labelRow}>
                  <span>{sections.whyThisPage.label}</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  {sections.whyThisPage.titleLines.map((line, index) => (
                    <React.Fragment key={line}>
                      {line}
                      {index < sections.whyThisPage.titleLines.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </Typography>
                <Typography component="p" className={classes.body}>
                  {sections.whyThisPage.body}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <div className={classes.thesisCard}>
                  <div>
                    <div className={classes.thesisLabel}>KEY THESIS</div>
                    <Typography component="p" className={classes.thesisText}>
                      {thesis.text}
                    </Typography>
                  </div>
                  <div className={classes.keywordLine}>
                    {thesis.keywords.map((keyword, index) => (
                      <React.Fragment key={keyword}>
                        <span>{keyword}</span>
                        {index < thesis.keywords.length - 1 && <span>/</span>}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <Grid container spacing={6}>
              <Grid item xs={12} md={5}>
                <div className={classes.labelRow}>
                  <span>{sections.brandOverview.label}</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  {sections.brandOverview.titleLines.map((line, index) => (
                    <React.Fragment key={line}>
                      {line}
                      {index < sections.brandOverview.titleLines.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </Typography>
                <Typography component="p" className={classes.body}>
                  {sections.brandOverview.body}
                </Typography>
              </Grid>
              <Grid item xs={12} md={7}>
                <div className={classes.knowledgeGrid}>
                  {sections.brandOverview.knowledgeItems.map((item) => (
                    <div className={classes.knowledgeItem} key={item.title}>
                      <div className={classes.knowledgeTitle}>{item.title}</div>
                      <Typography component="p" className={classes.noteBody}>
                        {item.text}
                      </Typography>
                    </div>
                  ))}
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <div className={classes.labelRow}>
              <span>{sections.lineGuide.label}</span>
              <span className={classes.labelLine} />
            </div>
            <Typography component="h2" className={classes.heading}>
              {sections.lineGuide.titleLines.map((line, index) => (
                <React.Fragment key={line}>
                  {line}
                  {index < sections.lineGuide.titleLines.length - 1 && <br />}
                </React.Fragment>
              ))}
            </Typography>
            <div className={classes.brandGrid}>
              {sections.lineGuide.items.map((item) => (
                <div className={classes.brandItem} key={item.title}>
                  <div className={classes.brandTag}>{item.tag}</div>
                  <Typography component="h3" className={classes.brandTitle}>
                    {item.title}
                  </Typography>
                  <Typography component="p" className={classes.noteBody}>
                    {item.text}
                  </Typography>
                </div>
              ))}
            </div>
          </section>

          <section className={classes.section}>
            <Grid container spacing={6}>
              <Grid item xs={12} md={5}>
                <div className={classes.labelRow}>
                  <span>{sections.firstExperience.label}</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  {sections.firstExperience.titleLines.map((line, index) => (
                    <React.Fragment key={line}>
                      {line}
                      {index < sections.firstExperience.titleLines.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </Typography>
                <Typography component="p" className={classes.body}>
                  {sections.firstExperience.intro}
                </Typography>
              </Grid>
              <Grid item xs={12} md={7}>
                <div className={classes.listBlock}>
                  {sections.firstExperience.items.map((item) => (
                    <div className={classes.listItem} key={item.index}>
                      <div className={classes.listIndex}>{item.index}</div>
                      <div className={classes.listTitle}>{item.title}</div>
                      <Typography component="p" className={classes.noteBody}>
                        {item.text}
                      </Typography>
                    </div>
                  ))}
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <div className={classes.quoteBlock}>
              <div className={classes.quoteMark}>“</div>
              <Typography component="p" className={classes.quoteText}>
                {sections.quote.textLines.map((line, index) => (
                  <React.Fragment key={line}>
                    {line}
                    {index < sections.quote.textLines.length - 1 && <br />}
                  </React.Fragment>
                ))}
              </Typography>
            </div>
          </section>

          <section className={classes.section}>
            <div className={classes.labelRow}>
              <span>{sections.whyRent.label}</span>
              <span className={classes.labelLine} />
            </div>
            <Typography component="h2" className={classes.heading}>
              {sections.whyRent.titleLines.map((line, index) => (
                <React.Fragment key={line}>
                  {line}
                  {index < sections.whyRent.titleLines.length - 1 && <br />}
                </React.Fragment>
              ))}
            </Typography>
            <Grid container spacing={3}>
              {sections.whyRent.items.map((reason) => (
                <Grid item xs={12} md={4} key={reason.number}>
                  <div className={classes.card}>
                    <div>
                      <div className={classes.cardNumber}>{reason.number}</div>
                      <div className={classes.cardTitle}>{reason.title}</div>
                      <div className={classes.smallRule} />
                      <Typography component="p" className={classes.body}>
                        {reason.text}
                      </Typography>
                    </div>
                  </div>
                </Grid>
              ))}
            </Grid>
          </section>

          <section className={classes.section} id="archive-items">
            <Grid container spacing={6}>
              <Grid item xs={12} md={4}>
                <div className={classes.labelRow}>
                  <span>{sections.inventory.label}</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  {sections.inventory.titleLines.map((line, index) => (
                    <React.Fragment key={line}>
                      {line}
                      {index < sections.inventory.titleLines.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </Typography>
                <Typography component="p" className={classes.body}>
                  {sections.inventory.body}
                </Typography>
              </Grid>
              <Grid item xs={12} md={8}>
                <div className={classes.inventoryGrid}>
                  {sections.inventory.items.map((item) => (
                    <a
                      className={`${classes.inventoryCard} ${classes.inventoryCardLink}`}
                      href={item.href}
                      key={item.href}
                    >
                      <div>
                        <div className={classes.inventoryImage}>
                          {item.image ? (
                            <img
                              src={`${process.env.REACT_APP_IMAGE_URL || ""}${item.image}`}
                              alt={item.title}
                              className={classes.inventoryProductImage}
                            />
                          ) : (
                            <>
                              Product image
                              <br />
                              coming soon
                            </>
                          )}
                        </div>
                        <div className={classes.inventoryMeta}>{item.meta}</div>
                        <Typography component="h3" className={classes.inventoryTitle}>
                          {item.title}
                        </Typography>
                        <Typography component="p" className={classes.noteBody}>
                          {item.text}
                        </Typography>
                      </div>
                      <div style={{ marginTop: 20 }}>
                        <span className={classes.textLink}>
                          このアイテムを見る
                        </span>
                      </div>
                    </a>
                  ))}
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section} id="faq">
            <div className={classes.labelRow}>
              <span>{sections.faq.label}</span>
              <span className={classes.labelLine} />
            </div>
            <Typography component="h2" className={classes.heading}>
              {sections.faq.titleLines.map((line, index) => (
                <React.Fragment key={line}>
                  {line}
                  {index < sections.faq.titleLines.length - 1 && <br />}
                </React.Fragment>
              ))}
            </Typography>
            <div className={classes.faqGrid}>
              {sections.faq.items.map((faq) => (
                <div className={classes.faqItem} key={faq.question}>
                  <div className={classes.faqQuestion}>{faq.question}</div>
                  <Typography component="p" className={classes.noteBody}>
                    {faq.answer}
                  </Typography>
                </div>
              ))}
            </div>
          </section>

          <div className={classes.finalCta}>
            <div className={classes.stripe} />
            <Typography component="h2" className={classes.finalTitle}>
              {sections.finalCta.titleLines.map((line, index) => (
                <React.Fragment key={line}>
                  {line}
                  {index < sections.finalCta.titleLines.length - 1 && <br />}
                </React.Fragment>
              ))}
            </Typography>
            <Typography component="p" className={classes.heroBody} style={{ margin: "0 auto 32px" }}>
              {sections.finalCta.body}
            </Typography>
            <Button
              className={classes.primaryButton}
              component="a"
              href={sections.finalCta.buttonHref}
            >
              {sections.finalCta.buttonLabel}
            </Button>
          </div>
        </div>
      </Container>
    </div>
  );
}

export default function ArchiveGuideScreen() {
  const classes = useStyles();
  const { slug } = useParams();

  if (!slug) {
    return <ArchiveGuideIndex classes={classes} />;
  }

  const article = guideArticles.find((item) => item.slug === slug);

  if (!article) {
    return <ArchiveGuideNotFound classes={classes} />;
  }

  return <ArchiveGuideDetail classes={classes} article={article} />;
}
